import { Box, capitalize, Stack, Tooltip, Typography } from '@mui/material';
import type {
  RestoreJob,
  Account,
  AuditLog,
  AuditLogEntitiesInner,
  SourceAwsOrganizationalUnit,
  BackupJob,
  BackupPolicyConfig,
  EksCluster,
  FileSearchRecord,
  InventoryResource,
  Notification,
  ScanJob,
} from '@repo/api-gw-sdk';
import {
  AccountState,
  BackupStatus,
  ConditionOperatorEnum,
  JobStatus,
  NotificationStatus,
  PartialRestoreType,
  Provider,
  ResourceType,
  SecurityAssignmentStatus,
} from '@repo/api-gw-sdk';
import type { Row } from '@tanstack/react-table';
import { usePathname } from 'next/navigation';
import React, { useEffect, useMemo } from 'react';

import { AuditLogOperationCell } from '@/app/(account)/global-management/audit-logs/auditLogOperationCell';
import { JobResourceNameField } from '@/app/(dashboard)/jobs/[tab]/jobResourceNameField';
import { JobStatusIcon } from '@/app/(dashboard)/jobs/[tab]/jobStatusIcon';
import {
  getJobType as getJobType,
  JobTypeCell,
  backupJobTypeMapper,
  restoreJobTypeMapper,
  scanJobTypeMapper,
} from '@/app/(dashboard)/jobs/[tab]/jobTypeCell';
import { PartialRestoreDataField } from '@/app/(dashboard)/jobs/[tab]/partialRestoreDataField';
import { useRestoreOrgsAndFolders } from '@/app/(dashboard)/settings/restore-accounts/gcp-projects/useRestoreOrgsAndFolders';
import { useRestoreAccounts } from '@/app/(dashboard)/settings/restore-accounts/useRestoreAccounts';
import { useOrgsAndFolders } from '@/app/(dashboard)/settings/source-accounts/gcp-organizations-folders/useOrgsAndFolders';
import { useManagementGroups } from '@/app/(dashboard)/settings/source-accounts/management-groups/useManagementGroups';
import { HelpIcon } from '@/components/shared/helpIcon';
import {
  getAllCloudProviders,
  useCloudProviders,
} from '@/contexts/useCloudProviders';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';
import { useUser } from '@/contexts/useUser';
import { PreferencesKey } from '@/data/dal/preferencesKey';
import {
  AccountStatuses,
  ApplicationNameAliases,
  ApplicationsLogosWithAliases,
  AuditLogActions,
  BackupStatuses,
  CloudProviders,
  DatabaseEngineAliases,
  EksClusterStatuses,
  Environments,
  ErrorCodes,
  JobStatuses,
  KubeAccessStatuses,
  SecurityStatuses,
  SupportedEntityTypes,
} from '@/data/inventory/data';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { useDataClassFilterOptions } from '@/data/inventory/useClassificationCategories';
import { useSupportedResourceTypes } from '@/data/inventory/useSupportedResourceTypes';
import type { BackupVault } from '@/data/vaults/backupVault';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import {
  type FilterProperty,
  PropertyPolicyRelation,
  PropertyType,
} from '@/types/advanceFilter';
import { isDateValid, formatDurationCompact } from '@/utils/dateTime';
import { DATE_TIME_FORMAT, dayjs } from '@/utils/dayjs';
import { fileSizeFormatter } from '@/utils/fileSizeFormatter';
import { formatGcsRegion, formatRegionLabel } from '@/utils/gcsRegion';

import {
  FullPanelMultiSelectFilter,
  BackupStatusFilter,
  FreeTextFilter,
  MultiSelectFilter,
} from './filters';
import { PathCell } from './path/pathCell';
import { findPropertyInObject } from './propertiesUtils';
import { BasicResourceNameCell } from './resourceName/resourceNameCell';
import { ResourceNameCondition } from './resourceName/resourceNameCondition';
import { S3BucketBackupTypeField } from './s3BucketBackupType/s3BucketBackupTypeField';
import { S3BucketScanTypeField } from './s3BucketBackupType/s3BucketScanTypeField';
import { TagsConditionSelector } from './tags/tagsConditionSelector';
import { TagsFilter } from './tags/tagsFilter';
import { TemporalLinkField } from './temporalLinkField';
import { useResourceIdentifiers } from './useResourceIentifiers';

import { AccountCondition } from '../accounts/accountCondition';
import { CloudAccountCell } from '../accounts/cloudAccountCell';
import { EonRoleVersion } from '../accounts/eonRoleVersion';
import { JobRestoreAccountIndicator } from '../accounts/restore/jobRestoreAccountIndicator';
import { SourceAccountIndicator } from '../accounts/sourceAccountIndicator';
import { BackupPolicyTag } from '../backupPolicy/BackupPolicyTag';
import { CopyToClipboardWrapper } from '../buttons/copyToClipboardButton';
import {
  CloudProviderCircle,
  CloudProviderIndicator,
  getCloudProvider,
  ResourceTypeIndicator,
} from '../cloudProviderIndicator';
import { createCloudProviderCondition } from '../cloudProviders/createCloudProviderCondition';
import { MultiSelectConditionPanel } from '../conditions/conditionComponents';
import { ControlsColumnIndicators } from '../controls/controlIndicator';
import { DataClassTag } from '../dataClasses/dataClassTag';
import type { FileExplorerTableItem } from '../explorer/fileExplorerPage';
import {
  supportedNotificationTypes,
  NotificationStatuses,
  NotificationTypes,
} from '../notifications/data';
import { useSupportedRegions } from '../selectors/supportedRegions';
import { CircleImage } from '../shared/circleImage';
import { SnapshotIndicator } from '../snapshots/SnapshotIndicator';
import { CellActionsWrapper } from '../table/cellActionsWrapper';
import { Tag } from '../tag';
import { TagsElasticContainer } from '../tags/tagsElasticContainer';
import { OverflowTooltip } from '../tooltips/overflowTooltip';
import { getUserName, UserCircle } from '../users/userBadge';
import { VaultTag } from '../vaults/vaultTag';

const INLINE_FILTER_ITEMS_LIMIT = 10;

export const isJob = (
  entity: object
): entity is BackupJob | RestoreJob | ScanJob =>
  'jobExecutionDetails' in entity;

export const isRestoreJob = (
  entity: BackupJob | RestoreJob | ScanJob
): entity is RestoreJob => 'restoreType' in entity;

export const isBackupJob = (
  entity: BackupJob | RestoreJob | ScanJob
): entity is BackupJob => 'backupType' in entity;

export const isScanJob = (
  entity: BackupJob | RestoreJob | ScanJob
): entity is ScanJob => 'scanType' in entity;

export const id: FilterProperty<InventoryResource> = {
  name: 'id',
  displayName: '',
  type: PropertyType.String,
  group: 'primary',
  column: {
    enableHiding: false,
    enableColumnFilter: false,
  },
  field: {
    getTitle: () => 'Eon resource ID',
    Component: ({ entity }) => {
      const dal = useDAL();
      const { isEmployee } = useUser();
      const { isDev, isDemo } = useEnvironment();

      const { data: prefResponse } = dal.preferences.useUserPref(
        PreferencesKey.Preferences
      );

      if (isDev || (isEmployee && !isDemo && prefResponse?.value?.isDevMode)) {
        return (
          <CopyToClipboardWrapper textToCopy={entity.id}>
            <Typography>{entity.id}</Typography>
          </CopyToClipboardWrapper>
        );
      }
    },
  },
};

export const partialRestoreData: FilterProperty<RestoreJob> = {
  name: 'partialRestoreData',
  displayName: 'Restore data',
  type: PropertyType.String,
  group: '',
  field: {
    getTitle: (entity) => {
      switch (entity.partialRestoreData?.partialRestoreType) {
        case PartialRestoreType.AwsEbsVolumePartialRestoreData:
          return 'Volume';
        case PartialRestoreType.AzureDiskPartialRestoreData:
        case PartialRestoreType.GcpDiskPartialRestoreData:
          return 'Disk';
        case PartialRestoreType.FilesPartialRestoreData:
          return 'File path';
        default:
          return '';
      }
    },
    Component: ({ entity }) => {
      return (
        entity.partialRestoreData && <PartialRestoreDataField entity={entity} />
      );
    },
  },
};

const baseJobType: FilterProperty<BackupJob | RestoreJob | ScanJob> = {
  name: 'backupType',
  displayName: 'Job type',
  type: PropertyType.String,
  group: '',
  column: {
    enableHiding: false,
    cell: ({ row, table }) => {
      return <JobTypeCell row={row} table={table} />;
    },
  },
  field: {
    Component: ({ entity }) => {
      const jobType = getJobType(entity);
      return jobType && <Typography>{jobType}</Typography>;
    },
  },
};

export const restoreJobType: FilterProperty<RestoreJob> = {
  ...(baseJobType as FilterProperty<RestoreJob>),
  name: 'restoreType',
  filter: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body } = dal.jobs.restore.useListTypes();
      const options =
        body?.restoreJobTypes?.map((type) => ({
          id: type,
          name: restoreJobTypeMapper[type],
        })) || [];
      return <MultiSelectFilter {...props} options={options} />;
    },
    Badge: ({ conditionValue }) => {
      return (
        restoreJobTypeMapper[
          conditionValue as keyof typeof restoreJobTypeMapper
        ] || conditionValue
      );
    },
  },
};

export const backupJobType: FilterProperty<BackupJob> = {
  ...(baseJobType as FilterProperty<BackupJob>),
  filter: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body } = dal.jobs.backup.useListTypes();
      const options =
        body?.backupJobTypes?.map((type) => ({
          id: type,
          name: backupJobTypeMapper[type],
        })) || [];
      return <MultiSelectFilter {...props} options={options} />;
    },
    Badge: ({ conditionValue }) => {
      return (
        backupJobTypeMapper[
          conditionValue as keyof typeof backupJobTypeMapper
        ] || conditionValue
      );
    },
  },
};

export const scanJobType: FilterProperty<ScanJob> = {
  ...(baseJobType as FilterProperty<ScanJob>),
  name: 'scanType',
  filter: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body } = dal.jobs.scan.useListTypes();
      const options =
        body?.scanJobTypes?.map((type) => ({
          id: type,
          name: scanJobTypeMapper[type],
        })) || [];
      return <MultiSelectFilter {...props} options={options} />;
    },
    Badge: ({ conditionValue }) => {
      return (
        scanJobTypeMapper[conditionValue as keyof typeof scanJobTypeMapper] ||
        conditionValue
      );
    },
  },
};

export const duration: FilterProperty<BackupJob | RestoreJob | ScanJob> = {
  name: 'durationSeconds',
  displayName: 'Duration',
  type: PropertyType.String,
  group: '',
  column: {
    enableColumnFilter: false,
    cell: ({ row }) => {
      const durationSeconds = row.original.jobExecutionDetails.durationSeconds;
      return (
        durationSeconds && (
          <Typography
            sx={{
              textAlign: 'end',
            }}
          >
            {formatDurationCompact(durationSeconds)}
          </Typography>
        )
      );
    },
  },
  field: {
    Component: ({ entity }) => {
      const durationSeconds = entity.jobExecutionDetails.durationSeconds;
      return (
        durationSeconds && (
          <Typography>{formatDurationCompact(durationSeconds)}</Typography>
        )
      );
    },
  },
};

export const pointInTime: FilterProperty<BackupJob | RestoreJob> = {
  name: 'pointInTime',
  displayName: 'Snapshot time',
  type: PropertyType.String,
  group: '',
  column: {
    enableColumnFilter: false,
    cell: ({ row }) => {
      const entity = row.original;
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { vaults } = useBackupVaults();
      const vault = vaults?.find((v) => v.id === entity.vault?.id);

      return (
        entity.snapshotDetails?.pointInTime && (
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <SnapshotIndicator
              backgroundColor={vault?.backgroundColor || null}
            />
            <Typography>
              {dayjs
                .utc(entity.snapshotDetails.pointInTime)
                .format(DATE_TIME_FORMAT)}
            </Typography>
          </Stack>
        )
      );
    },
  },
};

export const cloudProvider: FilterProperty<InventoryResource> = {
  name: 'cloudProvider',
  displayName: 'Source cloud',
  type: PropertyType.String,
  policyRelation: PropertyPolicyRelation.SingleValue,
  group: 'source-location',
  propertyPredicate: (providers) => providers.length > 1,
  column: {
    enableSorting: false,
    cell: ({ row }) => {
      if (isJob(row.original)) {
        return null;
      }
      return (
        row.original.cloudProvider && (
          <CloudProviderIndicator cloudProvider={row.original.cloudProvider} />
        )
      );
    },
  },
  filter: {
    Component: (props) => {
      const providers = useCloudProviders();

      return (
        <MultiSelectFilter
          {...props}
          options={providers
            .map((provider) => ({
              id: provider,
              name: CloudProviders[provider].displayName,
              image: CloudProviders[provider].logo,
              imageDark: CloudProviders[provider].logoDark,
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((x) => ({
              id: x.id,
              name: x.name,
              Image: (
                <CircleImage alt={x.name} src={x.image} srcDark={x.imageDark} />
              ),
            }))}
        />
      );
    },
    Badge: ({ conditionValue }) => {
      return CloudProviders[conditionValue]?.displayName || conditionValue;
    },
  },
  condition: {
    Component: (props) => {
      const { isDemo, isDev } = useEnvironment();
      const providers = getAllCloudProviders(isDemo || isDev).filter(
        (provider) =>
          !props.supportedProviders?.length ||
          props.supportedProviders?.includes(provider)
      );

      return (
        <MultiSelectConditionPanel
          showAddNewButton={false}
          showSearchBox={false}
          isLoading={false}
          entityName='provider'
          options={providers.map((provider) => ({
            id: provider,
            name: CloudProviders[provider]?.displayName || provider,
            Image: (
              <CircleImage
                alt={CloudProviders[provider]?.displayName || provider}
                src={CloudProviders[provider]?.logo}
                srcDark={CloudProviders[provider]?.logoDark}
              />
            ),
          }))}
          {...props}
        />
      );
    },
  },
};

export const sourceCloudProvider: FilterProperty<BackupJob> = {
  name: 'sourceCloudProvider',
  displayName: 'Source cloud',
  type: PropertyType.String,
  group: 'backup-from',
  column: {
    cell: ({ row }) => {
      return (
        row.original.resourceDetails?.cloudProvider && (
          <CloudProviderIndicator
            cloudProvider={row.original.resourceDetails.cloudProvider}
          />
        )
      );
    },
  },
  field: {
    Component: ({ entity }) => {
      return (
        entity.resourceDetails?.cloudProvider && (
          <CloudProviderIndicator
            cloudProvider={entity.resourceDetails.cloudProvider}
          />
        )
      );
    },
  },
  filter: cloudProvider.filter,
};

export const backupCloudProvider: FilterProperty<BackupJob | RestoreJob> = {
  name: 'backupCloudProvider',
  displayName: 'Backup cloud',
  type: PropertyType.String,
  group: 'backup-to',
  column: {
    cell: ({ row }) => {
      return (
        row.original.vault?.vaultAttributes.cloudProvider && (
          <CloudProviderIndicator
            cloudProvider={row.original.vault.vaultAttributes.cloudProvider}
          />
        )
      );
    },
  },
  field: {
    Component: ({ entity }) => {
      return (
        entity.vault?.vaultAttributes.cloudProvider && (
          <CloudProviderIndicator
            cloudProvider={entity.vault.vaultAttributes.cloudProvider}
          />
        )
      );
    },
  },
  filter: cloudProvider.filter,
};

export const restoreCloudProvider: FilterProperty<RestoreJob> = {
  name: 'restoreCloudProvider',
  displayName: 'Restore cloud',
  type: PropertyType.String,
  group: 'restore-to',
  column: {
    cell: ({ row }) => {
      return (
        row.original.destinationDetails.cloudProvider && (
          <CloudProviderIndicator
            cloudProvider={row.original.destinationDetails.cloudProvider}
          />
        )
      );
    },
  },
  field: {
    Component: ({ entity }) => {
      return (
        entity.destinationDetails.cloudProvider && (
          <CloudProviderIndicator
            cloudProvider={entity.destinationDetails.cloudProvider}
          />
        )
      );
    },
  },
  filter: cloudProvider.filter,
};

export const providerResourceId: FilterProperty<InventoryResource> = {
  name: 'providerResourceId',
  displayName: 'Resource ID',
  type: PropertyType.String,
  group: 'primary',
  policyRelation: PropertyPolicyRelation.SingleValue,
  column: {
    cell: ({ row }) => {
      const id = row.original.providerResourceId;

      if (!id) {
        return null;
      }

      return (
        <CellActionsWrapper textToCopy={id}>
          <Stack
            sx={{
              overflow: 'hidden',
              maxWidth: '280px',
            }}
          >
            <OverflowTooltip
              text={id}
              renderContent={(text, ref) => (
                <Typography
                  className='truncate'
                  ref={ref}
                  sx={{
                    lineHeight: '24px',
                  }}
                >
                  {text}
                </Typography>
              )}
            />
          </Stack>
        </CellActionsWrapper>
      );
    },
  },
  field: {
    Component: ({ entity }) => {
      const id = entity.providerResourceId;

      if (!id) {
        return null;
      }

      return (
        <CopyToClipboardWrapper textToCopy={id}>
          <Stack
            sx={{
              overflow: 'hidden',
              maxWidth: '530px',
            }}
          >
            <OverflowTooltip
              text={id}
              renderContent={(text, ref) => (
                <Typography
                  className='truncate'
                  ref={ref}
                  sx={{
                    lineHeight: '24px',
                  }}
                >
                  {text}
                </Typography>
              )}
            />
          </Stack>
        </CopyToClipboardWrapper>
      );
    },
  },
  filter: {
    fullPanelComponent: {
      title: 'Resource ID',
      subtitle: 'To filter your resources, select one or more resource IDs',
      Component: (props) => {
        const dal = useDAL();
        const { data: body, isLoading } =
          dal.inventory.useListResourceIdentifiers();

        const names = useMemo(
          () =>
            Array.from(
              new Set((body?.resources || []).map((x) => x.providerId))
            ).sort(),
          [body]
        );

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='ID'
            isLoading={isLoading}
            options={names.map((x) => ({
              id: x,
              name: x,
            }))}
          />
        );
      },
    },
  },
  condition: {
    Component: (props) => {
      const { data: body, isLoading } = useResourceIdentifiers(
        props.supportedProviders,
        props.supportedResourceTypes
      );

      return (
        <MultiSelectConditionPanel
          showAddNewButton
          showSearchBox
          isLoading={isLoading}
          entityName='ID'
          options={(body?.resources || [])
            .map((x) => x.providerId)
            .map((x) => ({ id: x, name: x }))}
          {...props}
        />
      );
    },
  },
};

export const resourceId: FilterProperty<
  FileSearchRecord | BackupJob | RestoreJob | ScanJob
> = {
  name: 'resourceId',
  displayName: 'Resource ID',
  type: PropertyType.String,
  group: 'primary',
  policyRelation: PropertyPolicyRelation.SingleValue,
  column: {
    cell: ({ row }) => {
      const id = isJob(row.original)
        ? row.original.resourceDetails?.providerResourceId
        : row.original.providerResourceId;

      if (!id) {
        return null;
      }

      return (
        <CellActionsWrapper textToCopy={id}>
          <Stack
            sx={{
              overflow: 'hidden',
              maxWidth: '280px',
            }}
          >
            <OverflowTooltip
              text={id}
              renderContent={(text, ref) => (
                <Typography
                  className='truncate'
                  ref={ref}
                  sx={{
                    lineHeight: '24px',
                  }}
                >
                  {text}
                </Typography>
              )}
            />
          </Stack>
        </CellActionsWrapper>
      );
    },
  },
  field: {
    Component: ({ entity }) => {
      const id = isJob(entity)
        ? entity.resourceDetails?.providerResourceId
        : entity.providerResourceId;

      if (!id) {
        return null;
      }

      return (
        <CopyToClipboardWrapper textToCopy={id}>
          <Stack
            sx={{
              overflow: 'hidden',
              maxWidth: '530px',
            }}
          >
            <OverflowTooltip
              text={id}
              renderContent={(text, ref) => (
                <Typography
                  className='truncate'
                  ref={ref}
                  sx={{
                    lineHeight: '24px',
                  }}
                >
                  {text}
                </Typography>
              )}
            />
          </Stack>
        </CopyToClipboardWrapper>
      );
    },
  },
  filter: {
    fullPanelComponent: {
      title: 'Resource ID',
      subtitle: 'To filter your resources, select one or more resource IDs',
      Component: (props) => {
        const dal = useDAL();
        const { data: body, isLoading } =
          dal.inventory.useListResourceIdentifiers();

        const names = useMemo(
          () =>
            Array.from(
              new Set((body?.resources || []).map((x) => x.providerId))
            ).sort(),
          [body]
        );

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='ID'
            isLoading={isLoading}
            options={names.map((x) => ({
              id: x,
              name: x,
            }))}
          />
        );
      },
    },
  },
  condition: {
    Component: (props) => {
      const { data: body, isLoading } = useResourceIdentifiers(
        props.supportedProviders,
        props.supportedResourceTypes
      );

      return (
        <MultiSelectConditionPanel
          showAddNewButton
          showSearchBox
          isLoading={isLoading}
          entityName='ID'
          options={(body?.resources || [])
            .map((x) => x.providerId)
            .map((x) => ({ id: x, name: x }))}
          {...props}
        />
      );
    },
  },
};

const SourceAccountCloudProviderMapping: Partial<Record<Provider, string>> = {
  [Provider.Aws]: 'Source account',
  [Provider.Azure]: 'Source subscription',
  [Provider.Gcp]: 'Source project',
};

export const sourceAccountName: FilterProperty<
  InventoryResource | BackupJob | ScanJob
> = {
  name: 'accountDisplayName',
  displayName: 'Source account name',
  type: PropertyType.SingleValue,
  group: 'source-location',
  policyRelation: PropertyPolicyRelation.SingleValue,
  column: {
    enableSorting: false,
    cell: ({ row }: { row: Row<InventoryResource | BackupJob | ScanJob> }) => {
      const providerAccountId = isJob(row.original)
        ? row.original.resourceDetails?.providerAccountId
        : row.original.providerAccountId;

      return (
        providerAccountId && (
          <CellActionsWrapper textToCopy={providerAccountId}>
            <Stack
              sx={{
                overflow: 'hidden',
                maxWidth: '280px',
              }}
            >
              <SourceAccountIndicator
                providerAccountId={providerAccountId}
                showAccountId={false}
                copyable={false}
              />
            </Stack>
          </CellActionsWrapper>
        )
      );
    },
  },
  filter: {
    Component: (props) => {
      return <FreeTextFilter {...props} placeholder='Search by name' />;
    },
  },
};

export const sourceAccountId: FilterProperty<
  InventoryResource | BackupJob | ScanJob
> = {
  name: 'accountId',
  displayName: 'Source account ID',
  type: PropertyType.SingleValue,
  group: 'source-location',
  policyRelation: PropertyPolicyRelation.SingleValue,
  column: {
    enableSorting: false,
    cell: ({ row }: { row: Row<InventoryResource | BackupJob | ScanJob> }) => {
      return CloudAccountCell(row.original);
    },
  },
  field: {
    getTitle: (entity) => {
      const cloudProvider = isJob(entity)
        ? entity.resourceDetails?.cloudProvider
        : entity.cloudProvider;
      return cloudProvider && SourceAccountCloudProviderMapping[cloudProvider]
        ? SourceAccountCloudProviderMapping[cloudProvider]
        : 'Source account';
    },
    Component: ({ entity }) => {
      const providerAccountId = isJob(entity)
        ? entity.resourceDetails?.providerAccountId
        : entity.providerAccountId;

      return (
        providerAccountId && (
          <SourceAccountIndicator
            providerAccountId={providerAccountId}
            showCloudIndicator={!isJob(entity)}
          />
        )
      );
    },
  },
  filter: {
    fullPanelComponent: {
      title: 'Source account ID',
      subtitle: 'To filter your resources, select one or more source accounts',
      Component: (props) => {
        const { sourceAccounts } = useSourceAccounts();

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='account'
            isLoading={false}
            shouldShowId
            options={(sourceAccounts || []).map((account) => ({
              id: account.providerAccountId,
              name: account.name,
              Image: CloudProviders[
                account.sourceAccountAttributes.cloudProvider
              ] && (
                <CircleImage
                  alt={
                    CloudProviders[
                      account.sourceAccountAttributes.cloudProvider
                    ].displayName
                  }
                  src={
                    CloudProviders[
                      account.sourceAccountAttributes.cloudProvider
                    ].logo
                  }
                  className='my-[16px]'
                />
              ),
            }))}
          />
        );
      },
    },
    Badge: ({ conditionValue }) => {
      const { sourceAccounts } = useSourceAccounts();

      return (
        sourceAccounts?.find((x) => x.providerAccountId === conditionValue)
          ?.name || conditionValue
      );
    },
  },
  condition: {
    Component: (props) => {
      const { sourceAccounts } = useSourceAccounts();
      const filteredAccount = useMemo(() => {
        return sourceAccounts?.filter(
          (x) =>
            !props.supportedProviders?.length ||
            props.supportedProviders?.includes(
              x.sourceAccountAttributes.cloudProvider
            )
        );
      }, [sourceAccounts, props.supportedProviders]);

      return <AccountCondition {...props} accounts={filteredAccount} />;
    },
  },
};

export const resourceName: FilterProperty<InventoryResource> = {
  name: 'resourceName',
  displayName: 'Resource name',
  type: PropertyType.String,
  group: 'primary',
  policyRelation: PropertyPolicyRelation.String,
  column: {
    enableHiding: false,
    cell: ({ row }) => {
      return row.original.resourceName && <BasicResourceNameCell row={row} />;
    },
  },
  field: {
    Component: ({ entity }) => {
      return (
        entity.resourceName && <Typography>{entity.resourceName}</Typography>
      );
    },
  },
  filter: {
    fullPanelComponent: {
      title: 'Resource name',
      subtitle: 'To filter your resources, select one or more names',
      Component: (props) => {
        const dal = useDAL();
        const { data: body, isLoading } =
          dal.inventory.useListResourceIdentifiers();

        const names = useMemo(
          () =>
            Array.from(
              new Set((body?.resources || []).map((x) => x.name))
            ).sort(),
          [body]
        );

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='name'
            isLoading={isLoading}
            options={names.map((x) => ({
              id: x,
              name: x,
            }))}
          />
        );
      },
    },
  },
  condition: {
    Component: ResourceNameCondition,
  },
};

export const jobResourceName: FilterProperty<BackupJob | RestoreJob | ScanJob> =
  {
    name: 'resourceName',
    displayName: 'Resource name',
    type: PropertyType.String,
    group: '',
    column: {
      cell: ({ row }) => {
        const resourceName = row.original.resourceDetails?.resourceName;
        return resourceName && <Typography>{resourceName}</Typography>;
      },
    },
    field: {
      Component: ({ entity }) => {
        return (
          entity.resourceDetails?.resourceName && (
            <JobResourceNameField job={entity} />
          )
        );
      },
    },
    filter: {
      fullPanelComponent: {
        title: 'Resource name',
        subtitle: 'To filter your resources, select one or more names',
        Component: (props) => {
          const dal = useDAL();
          const { data: body, isLoading } =
            dal.inventory.useListResourceIdentifiers();

          const names = useMemo(
            () =>
              Array.from(
                new Set((body?.resources || []).map((x) => x.name))
              ).sort(),
            [body]
          );

          return (
            <FullPanelMultiSelectFilter
              {...props}
              entityName='name'
              isLoading={isLoading}
              options={names.map((x) => ({
                id: x,
                name: x,
              }))}
            />
          );
        },
      },
    },
  };

export const dateDiscovered: FilterProperty<InventoryResource> = {
  name: 'dateDiscovered',
  displayName: 'First discovered (UTC)',
  type: PropertyType.Date,
  group: 'backup-details',
  column: {
    cell: ({ row }) =>
      isDateValid(row.original.discoveredTime) ? (
        <Typography>
          {dayjs.utc(row.original.discoveredTime).format(DATE_TIME_FORMAT)}
        </Typography>
      ) : undefined,
  },
  field: {
    Component: ({ entity }) =>
      isDateValid(entity.discoveredTime) ? (
        <Typography>
          {dayjs.utc(entity.discoveredTime).format(DATE_TIME_FORMAT)}
        </Typography>
      ) : undefined,
  },
};

export const createdTime: FilterProperty<InventoryResource> = {
  name: 'createdTime',
  displayName: 'Created time (UTC)',
  type: PropertyType.Date,
  group: 'source-location',
  column: {
    cell: ({ row }) =>
      isDateValid(row.original.createdTime) ? (
        <Typography>
          {dayjs.utc(row.original.createdTime).format(DATE_TIME_FORMAT)}
        </Typography>
      ) : undefined,
  },
  field: {
    Component: ({ entity }) =>
      isDateValid(entity.createdTime) ? (
        <Typography>
          {dayjs.utc(entity.createdTime).format(DATE_TIME_FORMAT)}
        </Typography>
      ) : undefined,
  },
};

export const vaults: FilterProperty<InventoryResource> = {
  name: 'vaults',
  displayName: 'Vaults',
  type: PropertyType.MultipleValues,
  group: 'backup-details',
  column: {
    enableSorting: false,
    cell: ({ row }) => {
      if (!row.original?.vaults?.length) {
        return;
      }
      const uniqueIds = Array.from(
        new Set(row.original.vaults.map((x) => x.id))
      );
      return uniqueIds.map((id) => <VaultTag key={id} vault={id} />);
    },
  },
  field: {
    Component: ({ entity }) => {
      if (!entity.vaults?.length) {
        return;
      }
      const uniqueIds = Array.from(new Set(entity.vaults.map((x) => x.id)));
      return uniqueIds.map((id) => <VaultTag key={id} vault={id} />);
    },
  },
  filter: {
    Component: (props) => {
      const { vaults } = useBackupVaults();

      return <MultiSelectFilter {...props} options={getVaultOptions(vaults)} />;
    },
    fullPanelComponent: {
      title: 'Vaults',
      subtitle: 'To filter your resources, select one or more vaults',
      useShouldUseFullPanel: () => {
        const { vaults } = useBackupVaults();
        return vaults.length > INLINE_FILTER_ITEMS_LIMIT;
      },
      Component: (props) => {
        const { vaults, isLoading } = useBackupVaults();

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='vault'
            isLoading={isLoading}
            options={getVaultOptions(vaults)}
          />
        );
      },
    },
    Badge: ({ conditionValue }) => {
      const { vaults } = useBackupVaults();

      return vaults.find((x) => x.id === conditionValue)?.name || '...';
    },
  },
};

const getVaultOptions = (vaults: BackupVault[]) =>
  vaults.map((x) => ({
    id: x.id,
    name: '',
    Image: <VaultTag vault={x} />,
  }));

export const resourceType: FilterProperty<
  InventoryResource | BackupJob | ScanJob
> = {
  name: 'resourceType',
  displayName: 'Resource type',
  type: PropertyType.SingleValue,
  group: 'primary',
  policyRelation: PropertyPolicyRelation.SingleValue,
  column: {
    enableSorting: false,
    cell: ({ row }) => {
      const resourceType = isJob(row.original)
        ? row.original.resourceDetails?.resourceType
        : row.original.resourceType;

      if (!resourceType) {
        return null;
      }

      return <ResourceTypeIndicator resourceType={resourceType} />;
    },
  },
  field: {
    Component: ({ entity }) => {
      const resourceType = isJob(entity)
        ? entity.resourceDetails?.resourceType
        : entity.resourceType;

      if (!resourceType) {
        return null;
      }

      return <ResourceTypeIndicator resourceType={resourceType} />;
    },
  },
  filter: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body } = dal.inventory.resourceTypes.useList();

      const resourceTypes =
        body?.resourceTypes.map((x) => ({
          id: x,
          name: ResourceTypes[x]?.displayName,
          image: CloudProviders[ResourceTypes[x]?.provider]?.logo,
          imageDark: CloudProviders[ResourceTypes[x]?.provider]?.logoDark,
          cloudProvider: CloudProviders[ResourceTypes[x]?.provider],
        })) || [];

      return (
        <MultiSelectFilter
          {...props}
          options={resourceTypes
            .sort(
              (a, b) =>
                (a.cloudProvider?.displayName || '').localeCompare(
                  b.cloudProvider?.displayName || ''
                ) || a.name?.localeCompare(b.name)
            )
            .map((x) => ({
              id: x.id,
              name: x.name,
              Image: x.image && (
                <CircleImage alt={x.name} src={x.image} srcDark={x.imageDark} />
              ),
            }))}
        />
      );
    },
    Badge: ({ conditionValue }) => {
      return ResourceTypes[conditionValue]?.displayName;
    },
  },
  condition: {
    Component: (props) => {
      const resourceTypes = useSupportedResourceTypes(
        props.supportedProviders
      ).filter(
        (x) =>
          !props.supportedResourceTypes?.length ||
          props.supportedResourceTypes?.includes(x)
      );

      return (
        <MultiSelectConditionPanel
          showAddNewButton={false}
          showSearchBox={false}
          isLoading={false}
          entityName='resource type'
          options={resourceTypes.map((x) => ({
            id: x,
            name: ResourceTypes[x]?.displayName || x,
            Image: (
              <CircleImage
                alt={ResourceTypes[x]?.displayName || x}
                src={CloudProviders[ResourceTypes[x]?.provider]?.logo}
                srcDark={CloudProviders[ResourceTypes[x]?.provider]?.logoDark}
              />
            ),
          }))}
          {...props}
        />
      );
    },
  },
};

export const resourceGroupName: FilterProperty<InventoryResource> = {
  name: 'resourceGroupName',
  displayName: 'Resource group',
  type: PropertyType.String,
  policyRelation: PropertyPolicyRelation.SingleValue,
  group: 'source-location',
  propertyPredicate: (providers) => providers.includes(Provider.Azure),
  column: {
    enableHiding: true,
    enableColumnFilter: true,
    enableSorting: true,
    cell: ({ row }) =>
      row.original.resourceGroupName && (
        <Typography>{row.original.resourceGroupName}</Typography>
      ),
  },
  field: {
    Component: ({ entity }) =>
      entity.resourceGroupName && (
        <CopyToClipboardWrapper textToCopy={entity.resourceGroupName}>
          <Typography>{entity.resourceGroupName}</Typography>
        </CopyToClipboardWrapper>
      ),
  },
  filter: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body } = dal.inventory.resourceGroups.useList();

      return (
        <MultiSelectFilter
          {...props}
          options={(body?.resourceGroups || []).map((rg) => ({
            id: rg,
            name: rg,
          }))}
        />
      );
    },
  },
  condition: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body, isLoading } = dal.inventory.resourceGroups.useList();
      return (
        <MultiSelectConditionPanel
          showAddNewButton
          showSearchBox
          isLoading={isLoading}
          entityName='resource group'
          options={(body?.resourceGroups || []).map((x) => ({
            id: x,
            name: x,
          }))}
          {...props}
        />
      );
    },
  },
};

export const apps: FilterProperty<InventoryResource | FileSearchRecord> = {
  name: 'apps',
  displayName: 'Apps',
  type: PropertyType.MultipleValues,
  group: 'classifications',
  policyRelation: PropertyPolicyRelation.MultipleValues,
  column: {
    enableSorting: false,
    cell: ({ row }) => {
      const apps =
        ('apps' in row.original
          ? row.original.apps
          : 'classifications' in row.original
            ? row.original.classifications?.appsDetails?.apps?.map(
                (x) => x.name
              )
            : undefined) || [];

      return (
        !!apps.length &&
        apps.map((name) => (
          <Tooltip
            key={name}
            title={
              <Typography
                variant='body2'
                component='span'
                className='text-inherit'
              >
                {ApplicationNameAliases[name] ?? name}
              </Typography>
            }
          >
            <CircleImage alt={name} src={ApplicationsLogosWithAliases[name]} />
          </Tooltip>
        ))
      );
    },
  },
  field: {
    Component: ({ entity }) => {
      const apps =
        ('apps' in entity
          ? entity.apps
          : 'classifications' in entity
            ? entity.classifications?.appsDetails?.apps?.map((x) => x.name)
            : undefined) || [];

      return (
        !!apps.length &&
        apps.map((name) => (
          <div key={name} className='flex items-center'>
            <CircleImage
              alt={name}
              src={ApplicationsLogosWithAliases[name]}
              className='mr-[8px]'
            />
            <Typography>{name}</Typography>
          </div>
        ))
      );
    },
  },
  filter: {
    fullPanelComponent: {
      title: 'Apps',
      subtitle: 'To filter your resources, select one or more apps',
      Component: (props) => {
        const dal = useDAL();
        const { data: body, isLoading } = dal.inventory.apps.useList();

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='app'
            isLoading={isLoading}
            options={(body?.apps || []).map((x) => ({
              id: x,
              name: x,
              Image: (
                <CircleImage
                  key={x}
                  alt={x}
                  src={ApplicationsLogosWithAliases[x]}
                />
              ),
            }))}
          />
        );
      },
    },
  },
  condition: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body, isLoading } = dal.inventory.apps.useKnownList();

      return (
        <MultiSelectConditionPanel
          showAddNewButton={false}
          showSearchBox
          isLoading={isLoading}
          entityName='app'
          useImagesInSelectedView
          options={(body?.apps ?? []).map((value) => ({
            id: value,
            name: value,
            Image: (
              <CircleImage
                key={value}
                alt={value}
                src={ApplicationsLogosWithAliases[value]}
              />
            ),
          }))}
          {...props}
        />
      );
    },
  },
};

export const securityScanConclusion: FilterProperty<InventoryResource> = {
  name: 'securityScanConclusion',
  displayName: 'Security scan',
  type: PropertyType.String,
  group: '',
  column: {
    enableHiding: false,
    enableColumnFilter: false,
  },
  filter: {
    shouldHideBadge: () => true,
  },
};

export const securityAssignmentStatus: FilterProperty<InventoryResource> = {
  name: 'threatDetection',
  displayName: 'Threat detection',
  type: PropertyType.SingleValue,
  group: 'backup-details',
  column: {
    enableSorting: false,
    cell: ({ row }) =>
      !!row.original.threatDetection && (
        <Typography>
          {SecurityStatuses[row.original.threatDetection]?.name}
        </Typography>
      ),
  },
  filter: {
    Component: (props) => {
      const options = Object.entries(SecurityStatuses)
        .filter(
          ([key]) => key !== SecurityAssignmentStatus.SECURITY_UNSPECIFIED
        )
        .map(([key, value]) => ({
          id: key,
          name: value.name,
        }));

      return <MultiSelectFilter {...props} options={options} />;
    },
    Badge({ conditionValue }) {
      return SecurityStatuses[conditionValue]?.name || conditionValue;
    },
  },
};

export const dataClasses: FilterProperty<InventoryResource> = {
  name: 'dataClasses',
  displayName: 'Data classes',
  type: PropertyType.MultipleValues,
  policyRelation: PropertyPolicyRelation.MultipleValues,
  group: 'classifications',
  column: {
    enableSorting: false,
    cell: ({ row }) =>
      !!row.original.classifications?.dataClassesDetails?.dataClasses
        ?.length && (
        <Stack
          direction='row'
          sx={{
            gap: '8px',
          }}
        >
          {row.original.classifications.dataClassesDetails.dataClasses.map(
            (x) => (
              <DataClassTag key={x} name={x} />
            )
          )}
        </Stack>
      ),
  },
  field: {
    Component: ({ entity }) =>
      !!entity.classifications?.dataClassesDetails?.dataClasses?.length && (
        <Stack
          direction='row'
          sx={{
            gap: '8px',
          }}
        >
          {entity.classifications.dataClassesDetails.dataClasses.map((x) => (
            <DataClassTag key={x} name={x} />
          ))}
        </Stack>
      ),
  },
  filter: {
    Component: (props) => {
      const { options } = useDataClassFilterOptions();
      return <MultiSelectFilter {...props} options={options} />;
    },
  },
  condition: {
    Component: (props) => {
      const { options, isLoading } = useDataClassFilterOptions();

      return (
        <MultiSelectConditionPanel
          showAddNewButton={false}
          showSearchBox={false}
          isLoading={isLoading}
          entityName='data class'
          options={options}
          {...props}
        />
      );
    },
  },
};

export const sensitivityAnnotations: FilterProperty<InventoryResource> = {
  name: 'sensitivityLabels',
  displayName: 'Sensitivity labels',
  type: PropertyType.SingleValue,
  policyRelation: PropertyPolicyRelation.Boolean,
  group: 'classifications',
  column: {
    enableSorting: false,
    cell: ({ row }) => (
      <Tag
        variant='primary'
        content={
          row.original.classifications?.sensitivityLabelsDetails
            ?.hasSensitivityLabels
            ? 'Detected'
            : 'Not detected'
        }
      />
    ),
  },
  filter: {
    Component: (props) => (
      <MultiSelectFilter
        {...props}
        options={[
          { id: 'true', name: 'Detected' },
          { id: 'false', name: 'Not detected' },
        ]}
      />
    ),
    Badge: ({ conditionValue }) => {
      return conditionValue === 'true' ? 'Detected' : 'Not detected';
    },
  },
  condition: {
    Component: ({ condition, onChange }) => {
      useEffect(() => {
        if (!condition.value?.length) {
          onChange({ ...condition, value: ['true'] });
        }
      }, [condition, onChange]);

      return null;
    },
  },
};

export const vmSize: FilterProperty<RestoreJob> = {
  name: 'vmSize',
  displayName: 'VM size',
  type: PropertyType.String,
  group: '',
  field: {
    Component: ({ entity }) => {
      const vmSize = findPropertyInObject<string>(
        entity.configuration,
        'vmSize'
      );
      return vmSize && <Typography>{vmSize}</Typography>;
    },
  },
};

export const networkInterface: FilterProperty<RestoreJob> = {
  name: 'networkInterface',
  displayName: 'Network interface',
  type: PropertyType.String,
  group: '',
  field: {
    Component: ({ entity }) => {
      const networkInterface = findPropertyInObject<string>(
        entity.configuration,
        'networkInterface'
      );
      return networkInterface && <Typography>{networkInterface}</Typography>;
    },
  },
};

export const zone: FilterProperty<RestoreJob> = {
  name: 'zone',
  displayName: 'Zone',
  type: PropertyType.String,
  group: '',
  field: {
    Component: ({ entity }) => {
      const zone = findPropertyInObject<string>(entity.configuration, 'zone');
      return zone && <Typography>{zone}</Typography>;
    },
  },
};

export const machineType: FilterProperty<RestoreJob> = {
  name: 'machineType',
  displayName: 'Machine type',
  type: PropertyType.String,
  group: '',
  field: {
    Component: ({ entity }) => {
      const machineType = findPropertyInObject<string>(
        entity.configuration,
        'machineType'
      );
      return machineType && <Typography>{machineType}</Typography>;
    },
  },
};

export const subnetGroup: FilterProperty<RestoreJob> = {
  name: 'subnetGroup',
  displayName: 'Subnet group',
  type: PropertyType.String,
  group: '',
  field: {
    Component: ({ entity }) => {
      const subnetGroup = findPropertyInObject<string>(
        entity.configuration,
        'subnetGroup'
      );
      return (
        subnetGroup && (
          <CopyToClipboardWrapper textToCopy={subnetGroup}>
            <Typography>{subnetGroup}</Typography>
          </CopyToClipboardWrapper>
        )
      );
    },
  },
};

export const tags: FilterProperty<InventoryResource | RestoreJob> = {
  name: 'tags',
  displayName: 'Tags',
  type: PropertyType.MultipleValues,
  policyRelation: PropertyPolicyRelation.Tags,
  group: 'primary',
  column: {
    enableSorting: false,
    cell: ({ row }) =>
      !isJob(row.original) && <TagsElasticContainer tags={row.original.tags} />,
  },
  field: {
    getTitle: (entity) =>
      getCloudProvider(entity) === Provider.Gcp ? 'Labels' : 'Tags',
    Component: ({ entity }) => {
      const tagsProperty =
        getCloudProvider(entity) === Provider.Gcp ? 'labels' : 'tags';
      const tags = isJob(entity)
        ? findPropertyInObject<Record<string, string>>(
            entity.configuration,
            tagsProperty
          )
        : entity.tags;
      return (
        tags &&
        !!Object.entries(tags || {}).length && (
          <TagsElasticContainer tags={tags} expandable />
        )
      );
    },
  },
  filter: {
    fullPanelComponent: {
      title: 'Tags',
      Component: (props) => (
        <TagsFilter {...props} initialFilterMode='key-value' />
      ),
    },
  },
};

export const tagKeys: FilterProperty<InventoryResource> = {
  name: 'tagKeys',
  displayName: 'Tag keys',
  type: PropertyType.MultipleValues,
  policyRelation: PropertyPolicyRelation.MultipleValues,
  group: 'primary',
  condition: {
    Component: (props) => (
      <TagsConditionSelector
        {...props}
        conditions={[props.condition]}
        initialFilterMode='key'
        property={tagKeys}
        onChange={(conditions) =>
          conditions.length
            ? props.onChange(conditions[0])
            : props.onChange({ ...props.condition, value: [] })
        }
      />
    ),
  },
};

export const tagKeyValues: FilterProperty<InventoryResource> = {
  name: 'tagKeyValues',
  displayName: 'Tag key-value pairs',
  type: PropertyType.MultipleValues,
  policyRelation: PropertyPolicyRelation.MultipleValues,
  group: 'primary',
  condition: {
    Component: (props) => (
      <TagsConditionSelector
        {...props}
        conditions={[props.condition]}
        initialFilterMode='key-value'
        property={tagKeyValues}
        onChange={(conditions) =>
          conditions.length
            ? props.onChange(conditions[0])
            : props.onChange({ ...props.condition, value: [] })
        }
      />
    ),
  },
};

export const sourceAccountTagKeys: FilterProperty<InventoryResource> = {
  name: 'sourceAccountTagKeys',
  displayName: 'Source account tag keys',
  type: PropertyType.MultipleValues,
  policyRelation: PropertyPolicyRelation.MultipleValues,
  group: 'primary',
  condition: {
    Component: (props) => (
      <TagsConditionSelector
        {...props}
        conditions={[props.condition]}
        initialFilterMode='key'
        dataSource='sourceAccountTags'
        property={sourceAccountTagKeys}
        onChange={(conditions) =>
          conditions.length
            ? props.onChange(conditions[0])
            : props.onChange({ ...props.condition, value: [] })
        }
      />
    ),
  },
};

export const sourceAccountTagKeyValues: FilterProperty<InventoryResource> = {
  name: 'sourceAccountTagKeyValues',
  displayName: 'Source account tag key-value pairs',
  type: PropertyType.MultipleValues,
  policyRelation: PropertyPolicyRelation.MultipleValues,
  group: 'primary',
  condition: {
    Component: (props) => (
      <TagsConditionSelector
        {...props}
        conditions={[props.condition]}
        initialFilterMode='key-value'
        dataSource='sourceAccountTags'
        property={sourceAccountTagKeyValues}
        onChange={(conditions) =>
          conditions.length
            ? props.onChange(conditions[0])
            : props.onChange({ ...props.condition, value: [] })
        }
      />
    ),
  },
};

export const environment: FilterProperty<InventoryResource | FileSearchRecord> =
  {
    name: 'environment',
    displayName: 'Environment',
    type: PropertyType.SingleValue,
    group: 'classifications',
    policyRelation: PropertyPolicyRelation.SingleValue,
    column: {
      cell: ({ row }) => {
        const environment =
          ('environment' in row.original
            ? row.original.environment
            : 'classifications' in row.original
              ? row.original.classifications?.environmentDetails?.environment
              : undefined) || '';

        return (
          environment &&
          Environments[environment] && (
            <Typography>{Environments[environment].title}</Typography>
          )
        );
      },
    },
    field: {
      Component: ({ entity }) => {
        const environment =
          ('environment' in entity
            ? entity.environment
            : 'classifications' in entity
              ? entity.classifications?.environmentDetails?.environment
              : undefined) || '';

        return (
          environment &&
          Environments[environment] && (
            <Typography>{Environments[environment].title}</Typography>
          )
        );
      },
    },
    filter: {
      Component: (props) => {
        const dal = useDAL();
        const { data: body } = dal.inventory.environments.useList();

        return (
          <MultiSelectFilter
            {...props}
            options={(body?.environments || []).map((x) => ({
              id: x,
              name: Environments[x]?.title,
              description: Environments[x]?.description,
            }))}
          />
        );
      },

      Badge: ({ conditionValue }) => {
        return Environments[conditionValue]?.title;
      },
    },
    condition: {
      Component: (props) => {
        const options = Object.entries(Environments).map(([key, value]) => ({
          id: key,
          name: value.title,
          description: value.description,
        }));

        return (
          <MultiSelectConditionPanel
            showAddNewButton={false}
            showSearchBox={false}
            isLoading={false}
            entityName='environment'
            options={options}
            {...props}
          />
        );
      },
    },
  };

const getBackupPolicesTags = (backupPolicies: BackupPolicyConfig[]) =>
  !!backupPolicies.length && (
    <Stack
      direction='row'
      sx={{
        gap: '8px',
      }}
    >
      {backupPolicies
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((policy) => (
          <BackupPolicyTag key={policy.id} policy={policy} />
        ))}
    </Stack>
  );

export const backupPolicies: FilterProperty<InventoryResource> = {
  name: 'backupPolicies',
  displayName: 'Active backup policies',
  type: PropertyType.MultipleValues,
  group: 'backup-details',
  column: {
    enableSorting: false,
    cell: ({ row }) => getBackupPolicesTags(row.original.backupPolicies),
  },
  field: {
    Component: ({ entity }) => getBackupPolicesTags(entity.backupPolicies),
  },
  filter: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body } = dal.backupPolicy.useList();

      return (
        <MultiSelectFilter
          {...props}
          options={getActiveBackupPolicyOptions(body)}
        />
      );
    },
    fullPanelComponent: {
      title: 'Active backup policies',
      subtitle: 'To filter your resources, select one or more backup policies',
      useShouldUseFullPanel: () => {
        const dal = useDAL();
        const { data: body } = dal.backupPolicy.useList();
        return (body?.backupPolicies || []).length > INLINE_FILTER_ITEMS_LIMIT;
      },
      Component: (props) => {
        const dal = useDAL();
        const { data: body, isLoading } = dal.backupPolicy.useList();

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='backup policy'
            isLoading={isLoading}
            options={getActiveBackupPolicyOptions(body)}
          />
        );
      },
    },
    Badge: ({ conditionValue }) => {
      const dal = useDAL();
      const { data: body } = dal.backupPolicy.useList();

      return (
        body?.backupPolicies.find((x) => x.id === conditionValue)?.name || '...'
      );
    },
  },
};

const getActiveBackupPolicyOptions = (
  body: { backupPolicies: BackupPolicyConfig[] } | undefined
) =>
  (body?.backupPolicies || []).map((x) => ({
    id: x.id,
    name: '',
    Image: <BackupPolicyTag policy={x} />,
  }));

const ObjectStorageResourceTypes: ResourceType[] = [
  ResourceType.AwsS3,
  ResourceType.GcpCloudStorageBucket,
];

const objectStoreName = (resourceType: ResourceType) =>
  resourceType === ResourceType.AwsS3 ? 'S3' : 'GCS';

export const sourceStorageObjectCount: FilterProperty<InventoryResource> = {
  name: 'sourceStorageObjectCount',
  displayName: 'Source object count',
  type: PropertyType.String,
  group: 'backup-details',
  field: {
    Component: ({ entity }) =>
      (entity.sourceStorage?.objectCount !== undefined ||
        (ObjectStorageResourceTypes.includes(entity.resourceType) &&
          entity.backupStatus !== BackupStatus.InitialClassification)) && (
        <Typography>
          {entity.sourceStorage?.objectCount?.toLocaleString() || 0}
        </Typography>
      ),
  },
};

export const VolumesCloudProviderMapping: Record<string, string> = {
  [Provider.Aws]: 'Volumes',
  [Provider.Azure]: 'Disks',
  [Provider.Gcp]: 'Disks',
};

export const sourceStorageSize: FilterProperty<InventoryResource | BackupJob> =
  {
    name: 'sourceStorageSize',
    displayName: 'Source storage size',
    type: PropertyType.String,
    group: 'backup-details',
    column: {
      enableColumnFilter: false,
      cell: ({ row }) => {
        if (isJob(row.original)) {
          if (row.original.resourceDetails?.sourceStorageSizeBytes) {
            return (
              <Typography>
                {fileSizeFormatter(
                  row.original.resourceDetails.sourceStorageSizeBytes
                )}
              </Typography>
            );
          }

          return null;
        }

        if (
          row.original.backupStatus === BackupStatus.InitialClassification &&
          (!row.original.sourceStorage?.sizeBytes ||
            row.original.sourceStorage.sizeBytes === 0)
        ) {
          return null;
        }

        const files = row.original.sourceStorage.filesCount;
        const volumes = row.original.sourceStorage.volumeCount;

        return (
          <Tooltip
            title={
              !!(files || volumes) && (
                <Typography component='span' className='text-inherit'>
                  {!!files && <div>{`# Files: ${files.toLocaleString()}`}</div>}
                  {!!volumes && (
                    <div>
                      {`# ${VolumesCloudProviderMapping[row.original.cloudProvider] || 'Volumes'}: ${volumes}`}
                    </div>
                  )}
                </Typography>
              )
            }
          >
            <Typography>
              {fileSizeFormatter(
                row.original.sourceStorage.sizeBytes,
                1,
                false
              )}
            </Typography>
          </Tooltip>
        );
      },
    },
    field: {
      Component: ({ entity }) => {
        if (isJob(entity)) {
          return null;
        }

        const isObjectStore = ObjectStorageResourceTypes.includes(
          entity.resourceType
        );

        if (
          entity.sourceStorage.sizeBytes ||
          (isObjectStore &&
            entity.backupStatus !== BackupStatus.InitialClassification)
        ) {
          return (
            <Stack
              direction='row'
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography>
                {fileSizeFormatter(entity.sourceStorage.sizeBytes, 1, false)}
              </Typography>
              {isObjectStore && (
                <HelpIcon
                  alwaysVisible
                  title="Total size of all objects in the bucket. This can include objects Eon doesn't back up."
                />
              )}
            </Stack>
          );
        }
        return null;
      },
    },
  };

export const standardStorageSize: FilterProperty<InventoryResource> = {
  name: 'standardStorageSize',
  displayName: 'Standard storage size',
  type: PropertyType.String,
  group: 'backup-details',
  field: {
    Component: ({ entity }) => {
      if (
        !ObjectStorageResourceTypes.includes(entity.resourceType) ||
        entity.backupStatus === BackupStatus.InitialClassification
      ) {
        return null;
      }
      return (
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography>
            {fileSizeFormatter(
              entity.sourceStorage.standardStorageSizeBytes,
              1,
              false
            )}
          </Typography>
          <HelpIcon
            alwaysVisible
            title={`Total size of all objects in the ${objectStoreName(entity.resourceType)} Standard storage class. This can include objects Eon doesn't back up, such as noncurrent versions and filtered objects.`}
          />
        </Stack>
      );
    },
  },
};

export const versioningEnabled: FilterProperty<InventoryResource> = {
  name: 'versioningEnabled',
  displayName: 'Object versioning',
  type: PropertyType.String,
  group: 'backup-details',
  field: {
    Component: ({ entity }) => {
      if (!ObjectStorageResourceTypes.includes(entity.resourceType)) {
        return null;
      }
      return (
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography>
            {entity.sourceStorage?.versioningEnabled ? 'Enabled' : 'Disabled'}
          </Typography>
          <HelpIcon
            alwaysVisible
            title={`Whether ${objectStoreName(entity.resourceType)} Versioning is enabled for the bucket. Eon reads only the current version when backing up.`}
          />
        </Stack>
      );
    },
  },
};

export const backupStorageSize: FilterProperty<InventoryResource> = {
  name: 'backupStorageSize',
  displayName: 'Eon snapshot size',
  type: PropertyType.String,
  group: 'backup-details',
  column: {
    enableColumnFilter: false,
    cell: ({ row }) =>
      !!row.original.snapshotStorage?.sizeBytes && (
        <Typography>
          {fileSizeFormatter(row.original.snapshotStorage.sizeBytes)}
        </Typography>
      ),
  },
  field: {
    Component: ({ entity }) =>
      !!entity.snapshotStorage?.sizeBytes && (
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography>
            {fileSizeFormatter(entity.snapshotStorage.sizeBytes)}
          </Typography>
          {ObjectStorageResourceTypes.includes(entity.resourceType) && (
            <HelpIcon
              alwaysVisible
              title={`Total size of all Eon snapshots of the bucket. Eon backs up objects only if they're the current version in the ${objectStoreName(entity.resourceType)} Standard storage class and they meet your filtering criteria, if applicable.`}
            />
          )}
        </Stack>
      ),
  },
};

export const genericSnapshotsDistinctDateCount: FilterProperty<InventoryResource> =
  {
    name: 'genericSnapshotsDistinctDateCount',
    displayName: 'Non-Eon snapshot count',
    type: PropertyType.String,
    group: 'backup-details',
    column: {
      enableColumnFilter: false,
      cell: ({ row }) => (
        <Typography>
          {(
            row.original.snapshotStorage?.nonEonSnapshotCount || 0
          ).toLocaleString()}
        </Typography>
      ),
    },
    field: {
      Component: ({ entity }) => (
        <Typography>
          {(entity.snapshotStorage?.nonEonSnapshotCount || 0).toLocaleString()}
        </Typography>
      ),
    },
  };

export const eonSnapshotsCount: FilterProperty<InventoryResource> = {
  name: 'eonSnapshotCount',
  displayName: 'Eon snapshot count',
  type: PropertyType.String,
  group: 'backup-details',
  column: {
    enableColumnFilter: false,
    cell: ({ row }) => (
      <Typography>
        {(row.original.snapshotStorage?.eonSnapshotCount || 0).toLocaleString()}
      </Typography>
    ),
  },
  field: {
    Component: ({ entity }) => (
      <Typography>
        {(entity.snapshotStorage?.eonSnapshotCount || 0).toLocaleString()}
      </Typography>
    ),
  },
};

// Each enum option maps to a backend condition on the cloud_resource table.
// Values must match the IssueType OpenAPI enum in IssueType.yaml.
const IssueTypes: Record<string, string> = {
  LAST_SNAPSHOT_PARTIAL: 'Last snapshot was partial',
};

export const issues: FilterProperty<InventoryResource> = {
  name: 'issues',
  displayName: 'Issues',
  type: PropertyType.SingleValue,
  group: 'backup-details',
  column: {
    cell: ({ row }) =>
      row.original.issuesCount ? (
        <Typography>{row.original.issuesCount.toLocaleString()}</Typography>
      ) : null,
  },
  filter: {
    Component: (props) => (
      <MultiSelectFilter
        {...props}
        options={Object.entries(IssueTypes).map(([id, name]) => ({
          id,
          name,
        }))}
      />
    ),
    Badge: ({ conditionValue }) => IssueTypes[conditionValue] || conditionValue,
  },
};

const VpcCloudProviderMapping: Record<string, string> = {
  Azure: 'Virtual network',
};

export const vpc: FilterProperty<InventoryResource> = {
  name: 'vpc',
  displayName: 'VPC',
  type: PropertyType.SingleValue,
  group: 'source-location',
  policyRelation: PropertyPolicyRelation.SingleValue,
  propertyPredicate: (providers) => providers.includes(Provider.Aws),
  column: {
    enableSorting: false,
    cell: ({ row }) =>
      row.original.vpc && (
        <CellActionsWrapper textToCopy={row.original.vpc}>
          <Stack
            sx={{
              overflow: 'hidden',
              maxWidth: '280px',
            }}
          >
            <OverflowTooltip
              text={row.original.vpc}
              renderContent={(text, ref) => (
                <Typography
                  className='truncate'
                  ref={ref}
                  sx={{
                    lineHeight: '24px',
                  }}
                >
                  {text}
                </Typography>
              )}
            />
          </Stack>
        </CellActionsWrapper>
      ),
  },
  field: {
    getTitle: (entity) =>
      VpcCloudProviderMapping[entity.cloudProvider] || 'VPC',
    Component: ({ entity }) =>
      entity.vpc && (
        <CopyToClipboardWrapper textToCopy={entity.vpc}>
          <Typography>{entity.vpc}</Typography>
        </CopyToClipboardWrapper>
      ),
  },
  filter: {
    fullPanelComponent: {
      title: 'VPC',
      subtitle: 'To filter your resources, select one or more VPCs',
      Component: (props) => {
        const dal = useDAL();
        const { data: body, isLoading } = dal.inventory.networks.useList();

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='vpc'
            isLoading={isLoading}
            options={(body?.networks || []).map((x) => ({
              id: x,
              name: x,
            }))}
          />
        );
      },
    },
  },
  condition: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body, isLoading } = dal.inventory.networks.useList();
      return (
        <MultiSelectConditionPanel
          showAddNewButton
          showSearchBox
          isLoading={isLoading}
          entityName='VPC'
          options={(body?.networks || []).map((x) => ({
            id: x,
            name: x,
          }))}
          {...props}
        />
      );
    },
  },
};

export const subnet: FilterProperty<RestoreJob> = {
  name: 'subnetId',
  displayName: 'Subnet',
  type: PropertyType.SingleValue,
  group: 'configuration',
  field: {
    Component: ({ entity }) => {
      const subnet = findPropertyInObject<string>(
        entity.configuration,
        'subnetId'
      );
      return subnet && <Typography>{subnet}</Typography>;
    },
  },
};

export const instanceType: FilterProperty<RestoreJob> = {
  name: 'instanceType',
  displayName: 'Instance Type',
  type: PropertyType.SingleValue,
  group: 'configuration',
  field: {
    Component: ({ entity }) => {
      const instanceType = findPropertyInObject<string>(
        entity.configuration,
        'instanceType'
      );
      return instanceType && <Typography>{instanceType}</Typography>;
    },
  },
};

export const securityGroups: FilterProperty<RestoreJob> = {
  name: 'securityGroups',
  displayName: 'Security Groups',
  type: PropertyType.MultipleValues,
  group: 'configuration',
  field: {
    Component: ({ entity }) => {
      const securityGroups = findPropertyInObject<string[]>(
        entity.configuration,
        'securityGroupIds'
      );
      return (
        securityGroups && <Typography>{securityGroups.join(', ')}</Typography>
      );
    },
  },
};

export const instanceProfile: FilterProperty<RestoreJob> = {
  name: 'instanceProfile',
  displayName: 'Instance Profile',
  type: PropertyType.SingleValue,
  group: 'configuration',
  field: {
    Component: ({ entity }) => {
      const instanceProfile = findPropertyInObject<string>(
        entity.configuration,
        'instanceProfileName'
      );
      return instanceProfile && <Typography>{instanceProfile}</Typography>;
    },
  },
};

export const encryptionKey: FilterProperty<RestoreJob> = {
  name: 'encryptionKey',
  displayName: 'Encryption Key',
  type: PropertyType.SingleValue,
  group: 'configuration',
  field: {
    Component: ({ entity }) => {
      const encryptionKey = findPropertyInObject<string>(
        entity.configuration,
        'encryptionKeyId'
      );
      return (
        encryptionKey && (
          <CopyToClipboardWrapper textToCopy={encryptionKey}>
            <Typography>{encryptionKey}</Typography>
          </CopyToClipboardWrapper>
        )
      );
    },
  },
};

export const databaseFilter: FilterProperty<RestoreJob> = {
  name: 'databaseFilter',
  displayName: 'Databases to restore',
  type: PropertyType.MultipleValues,
  group: 'configuration',
  field: {
    Component: ({ entity }) => {
      const databases = findPropertyInObject<string[]>(
        entity.configuration,
        'databaseFilter'
      );

      return (
        !!databases?.length && <Typography>{databases.join(', ')}</Typography>
      );
    },
  },
};

export const subnets: FilterProperty<InventoryResource> = {
  name: 'subnets',
  displayName: 'Subnets',
  type: PropertyType.MultipleValues,
  group: 'source-location',
  policyRelation: PropertyPolicyRelation.MultipleValues,
  column: {
    enableSorting: false,
    cell: ({ row }) =>
      !!row.original.subnets?.length && (
        <CellActionsWrapper textToCopy={row.original.subnets.join(', ')}>
          <Stack
            sx={{
              overflow: 'hidden',
              maxWidth: '280px',
            }}
          >
            <OverflowTooltip
              text={row.original.subnets.join(', ')}
              renderContent={(text, ref) => (
                <Typography
                  className='truncate'
                  ref={ref}
                  sx={{
                    lineHeight: '24px',
                  }}
                >
                  {text}
                </Typography>
              )}
            />
          </Stack>
        </CellActionsWrapper>
      ),
  },
  field: {
    Component: ({ entity }) =>
      !!entity.subnets?.length && (
        <CopyToClipboardWrapper textToCopy={entity.subnets.join(', ')}>
          <Typography>{entity.subnets.join(', ')}</Typography>
        </CopyToClipboardWrapper>
      ),
  },
  filter: {
    fullPanelComponent: {
      title: 'Subnets',
      subtitle: 'To filter your resources, select one or more subnets',
      Component: (props) => {
        const dal = useDAL();
        const { data: body, isLoading } = dal.inventory.subnets.useList();

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='subnet'
            isLoading={isLoading}
            options={(body?.subnets || []).map((x) => ({
              id: x,
              name: x,
            }))}
          />
        );
      },
    },
  },
  condition: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body, isLoading } = dal.inventory.subnets.useList();
      return (
        <MultiSelectConditionPanel
          showAddNewButton
          showSearchBox
          isLoading={isLoading}
          entityName='subnet'
          options={(body?.subnets || []).map((x) => ({
            id: x,
            name: x,
          }))}
          {...props}
        />
      );
    },
  },
};

export const lastBackup: FilterProperty<InventoryResource | FileSearchRecord> =
  {
    name: 'lastBackup',
    displayName: 'Last backup (UTC)',
    type: PropertyType.Date,
    group: 'backup-details',
    column: {
      enableColumnFilter: false,
      cell: ({ row }) => {
        const lastBackup =
          'lastBackup' in row.original && row.original.lastBackup;
        if (isDateValid(lastBackup)) {
          return (
            <Typography>
              {dayjs.utc(lastBackup).format(DATE_TIME_FORMAT)}
            </Typography>
          );
        }

        const generatedOn =
          'snapshots' in row.original &&
          row.original.snapshots?.length &&
          row.original.snapshots[0].generatedOn;

        if (isDateValid(generatedOn)) {
          return (
            <Typography>
              {dayjs.utc(generatedOn).format(DATE_TIME_FORMAT)}
            </Typography>
          );
        }
      },
    },
    field: {
      Component: ({ entity }) => {
        const lastBackup = 'lastBackup' in entity && entity.lastBackup;
        if (isDateValid(lastBackup)) {
          return (
            <Typography>
              {dayjs.utc(lastBackup).format(DATE_TIME_FORMAT)}
            </Typography>
          );
        }

        const generatedOn =
          'snapshots' in entity &&
          entity.snapshots?.length &&
          entity.snapshots[0].generatedOn;

        if (isDateValid(generatedOn)) {
          return (
            <Typography>
              {dayjs.utc(generatedOn).format(DATE_TIME_FORMAT)}
            </Typography>
          );
        }
      },
    },
  };

const formatInventoryRegion = (
  entity: InventoryResource | BackupJob | ScanJob,
  region: string | undefined
): string => {
  if (isJob(entity)) {
    return region ?? '';
  }
  const gcsProps = entity.resourceProperties?.gcpGcs;
  if (gcsProps) {
    return formatGcsRegion(
      gcsProps.location || region,
      gcsProps.locationType,
      gcsProps.constituentRegions
    );
  }
  return region ?? '';
};

export const sourceRegion: FilterProperty<
  InventoryResource | BackupJob | ScanJob
> = {
  name: 'sourceRegion',
  displayName: 'Source region',
  type: PropertyType.SingleValue,
  group: 'source-location',
  policyRelation: PropertyPolicyRelation.SingleValue,
  column: {
    enableSorting: false,
    cell: ({ row }) => {
      const region = isJob(row.original)
        ? row.original.resourceDetails?.region
        : row.original.region;
      const displayRegion = formatInventoryRegion(row.original, region);
      return displayRegion && <Typography>{displayRegion}</Typography>;
    },
  },
  field: {
    Component: ({ entity }) => {
      const region = isJob(entity)
        ? entity.resourceDetails?.region
        : entity.region;
      const displayRegion = formatInventoryRegion(entity, region);
      return displayRegion && <Typography>{displayRegion}</Typography>;
    },
  },
  filter: {
    fullPanelComponent: {
      title: 'Source region',
      subtitle: 'To filter your resources, select one or more regions',
      Component: (props) => {
        const dal = useDAL();
        const { data: body, isLoading } = dal.inventory.regions.useList();

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='region'
            isLoading={isLoading}
            options={(body?.regions || []).map((x) => ({
              id: x.name,
              name: formatRegionLabel(x.name),
            }))}
          />
        );
      },
    },
  },
  condition: {
    Component: (props) => {
      const supportedRegions = useSupportedRegions();
      const providers = props.supportedProviders || [];
      const options = [
        ...(!providers.length || providers.includes(Provider.Aws)
          ? supportedRegions[Provider.Aws] || []
          : []
        ).map((x) => ({
          id: x,
          name: formatRegionLabel(x),
          Image: <CloudProviderCircle cloudProvider={Provider.Aws} />,
        })),
        ...(!providers.length || providers.includes(Provider.Azure)
          ? supportedRegions[Provider.Azure] || []
          : []
        ).map((x) => ({
          id: x,
          name: formatRegionLabel(x),
          Image: <CloudProviderCircle cloudProvider={Provider.Azure} />,
        })),
        ...(!providers.length || providers.includes(Provider.Gcp)
          ? supportedRegions[Provider.Gcp] || []
          : []
        ).map((x) => ({
          id: x,
          name: formatRegionLabel(x),
          Image: <CloudProviderCircle cloudProvider={Provider.Gcp} />,
        })),
      ];
      return (
        <MultiSelectConditionPanel
          showAddNewButton={false}
          showSearchBox
          isLoading={false}
          entityName='region'
          options={options}
          {...props}
        />
      );
    },
  },
};

export const replicaRegions: FilterProperty<InventoryResource> = {
  name: 'replicaRegions',
  displayName: 'Replica regions',
  type: PropertyType.String,
  group: 'source-location',
  field: {
    Component: ({ entity }) => {
      const regions =
        entity.resourceProperties?.awsKeyspacesTable?.replicaRegions;
      return regions?.length ? (
        <Typography>{regions.join(', ')}</Typography>
      ) : undefined;
    },
  },
};

export const backupRegions: FilterProperty<InventoryResource> = {
  name: 'backupRegions',
  displayName: 'Backup regions',
  type: PropertyType.MultipleValues,
  group: 'backup-details',
  column: {
    enableSorting: false,
    cell: ({ row }) =>
      !!row.original.vaults.length && (
        <Typography>
          {Array.from(new Set(row.original.vaults.map((x) => x.region)))
            .sort((a, b) => a.localeCompare(b))
            .join(', ')}
        </Typography>
      ),
  },
  field: {
    Component: ({ entity }) =>
      !!entity.vaults.length && (
        <Typography>
          {Array.from(new Set(entity.vaults.map((x) => x.region)))
            .sort((a, b) => a.localeCompare(b))
            .join(', ')}
        </Typography>
      ),
  },
  filter: {
    fullPanelComponent: {
      title: 'Backup regions',
      subtitle: 'To filter your resources, select one or more regions',
      Component: (props) => {
        const dal = useDAL();
        const { data: body, isLoading } = dal.inventory.backupRegions.useList();

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='region'
            isLoading={isLoading}
            options={(body?.regions || []).map((x) => ({
              id: x.name,
              name: x.name,
            }))}
          />
        );
      },
    },
  },
};

export const backupRegion: FilterProperty<BackupJob | RestoreJob> = {
  name: 'backupRegion',
  displayName: 'Backup region',
  type: PropertyType.SingleValue,
  group: 'backup-to',
  column: {
    enableSorting: false,
    cell: ({ row }) => {
      const vault = row.original.vault;
      return vault && <Typography>{vault.region}</Typography>;
    },
  },
  field: {
    Component: ({ entity }) => {
      return entity.vault && <Typography>{entity.vault.region}</Typography>;
    },
  },
  filter: {
    fullPanelComponent: {
      title: 'Backup region',
      subtitle: 'To filter your jobs, select one or more regions.',
      Component: (props) => {
        const dal = useDAL();
        const { data: body, isLoading } = dal.inventory.backupRegions.useList();

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='region'
            isLoading={isLoading}
            options={(body?.regions || []).map((x) => ({
              id: x.name,
              name: x.name,
            }))}
          />
        );
      },
    },
  },
};

export const controlLevel: FilterProperty<InventoryResource> = {
  name: 'severity',
  displayName: 'Severity',
  type: PropertyType.String,
  group: '',
  column: {
    enableHiding: false,
    enableColumnFilter: false,
  },
  filter: {
    Badge: ({ conditionValue }) => {
      const value = conditionValue.toLowerCase();
      return `${capitalize(value)}${value === 'muted' ? '' : '-severity'} control violations`;
    },
  },
};

export const controlId: FilterProperty<InventoryResource> = {
  name: 'controls',
  displayName: 'Control',
  type: PropertyType.String,
  group: '',
  column: {
    enableHiding: false,
    enableColumnFilter: false,
  },
  filter: {
    Badge: ({ conditionValue }) => {
      const dal = useDAL();
      const { data: body } = dal.controls.useList();

      return body?.controls.find((x) => x.id === conditionValue)?.name || '...';
    },
  },
};

export const mutedControlId: FilterProperty<InventoryResource> = {
  name: 'mutedControls',
  displayName: 'Muted control',
  type: PropertyType.String,
  group: '',
  column: {
    enableHiding: false,
    enableColumnFilter: false,
  },
  filter: {
    Badge: ({ conditionValue }) => {
      const dal = useDAL();
      const { data: body } = dal.controls.useList();

      return body?.controls.find((x) => x.id === conditionValue)?.name || '...';
    },
  },
};

export const backupStatus: FilterProperty<InventoryResource> = {
  name: 'backupStatus',
  displayName: 'Backup status',
  type: PropertyType.String,
  group: '',
  linkedProperties: [controlLevel, controlId, mutedControlId],
  column: {
    minSize: 20,
    size: 20,
    enableHiding: false,
    enableSorting: false,
    cell: ({ row }) => {
      const status = BackupStatuses[row.original.backupStatus];
      if (!status) {
        return null;
      }
      return (
        <ControlsColumnIndicators
          backupStatus={row.original.backupStatus}
          controlViolations={row.original.controlViolationCounts}
        />
      );
    },
  },
  filter: {
    Component: (props) => {
      const path = usePathname();
      return (
        <BackupStatusFilter
          {...props}
          showAdditionalStatuses={path === '/inventory'}
        />
      );
    },
    shouldHideBadge: (condition) =>
      condition.operator === ConditionOperatorEnum.NotIn,
    Badge: (props) => {
      return BackupStatuses[props.conditionValue]?.title;
    },
  },
};

export const path: FilterProperty<FileSearchRecord> = {
  name: 'path',
  displayName: 'Path',
  type: PropertyType.String,
  group: 'source-location',
  column: {
    enableColumnFilter: false,
    cell: ({ row }) => {
      return row.original.path && <PathCell entity={row.original} />;
    },
  },
  field: {
    Component: ({ entity }) =>
      entity.path && <Typography>{entity.path}</Typography>,
  },
};

export const modifiedOn: FilterProperty<
  FileSearchRecord | FileExplorerTableItem
> = {
  name: 'modifiedOn',
  displayName: 'Modified on (UTC)',
  type: PropertyType.Date,
  group: 'primary',
  column: {
    cell: ({ row }) => {
      if (row.original.snapshots?.length) {
        return (
          <Typography>
            {dayjs
              .utc(row.original.snapshots[0].modifiedOn)
              .format(DATE_TIME_FORMAT)}
          </Typography>
        );
      }

      return (
        'modifiedOn' in row.original &&
        row.original.modifiedOn && (
          <Typography>
            {dayjs.utc(row.original.modifiedOn).format(DATE_TIME_FORMAT)}
          </Typography>
        )
      );
    },
  },
};

export const fileType: FilterProperty<FileSearchRecord> = {
  name: 'fileType',
  displayName: 'File type',
  type: PropertyType.String,
  group: 'primary',
  column: {
    cell: ({ row }) => {
      if (row.original.type === 'd') {
        return null;
      }

      const lastSeg = row.original.path.split('/').pop() || row.original.path;
      const parts = lastSeg.split('.');
      return <Typography>{parts.length > 1 ? parts.pop() : ''}</Typography>;
    },
  },
};

export const fileSize: FilterProperty<
  FileSearchRecord | FileExplorerTableItem
> = {
  name: 'fileSize',
  displayName: 'Size',
  type: PropertyType.String,
  group: 'primary',
  column: {
    cell: ({ row }) => {
      if (
        'snapshots' in row.original &&
        row.original.snapshots?.length &&
        row.original.snapshots[0].size
      ) {
        return (
          <Typography>
            {fileSizeFormatter(row.original.snapshots[0].size)}
          </Typography>
        );
      }

      return (
        'size' in row.original &&
        row.original.size && (
          <Typography>{fileSizeFormatter(row.original.size)} </Typography>
        )
      );
    },
  },
};

export const operationId: FilterProperty<AuditLog> = {
  name: 'operationId',
  displayName: 'Operation',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      row.original.operationName && <AuditLogOperationCell row={row} />,
  },
  filter: {
    Component: (props) => (
      <FreeTextFilter {...props} placeholder={'Search by ID'} />
    ),
  },
};

export const action: FilterProperty<AuditLog> = {
  name: 'action',
  displayName: 'Event type',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      row.original.action &&
      AuditLogActions[row.original.action] && (
        <Typography>{AuditLogActions[row.original.action].name}</Typography>
      ),
  },
  filter: {
    Component: (props) => {
      const options = Object.entries(AuditLogActions).map(([key, value]) => ({
        id: key,
        name: value.name,
      }));

      return <MultiSelectFilter {...props} options={options} />;
    },
    Badge: ({ conditionValue }) => {
      return AuditLogActions[conditionValue]?.name || conditionValue;
    },
  },
};

export const requestTime: FilterProperty<AuditLog> = {
  name: 'requestTime',
  displayName: 'Time (UTC)',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      row.original.requestDetails?.requestTime && (
        <Typography>
          {dayjs
            .utc(row.original.requestDetails.requestTime)
            .format(DATE_TIME_FORMAT)}
        </Typography>
      ),
  },
  filter: {
    shouldHideBadge: () => true,
  },
};

export const statusCode: FilterProperty<AuditLog> = {
  name: 'statusCode',
  displayName: 'Status code',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) => <Typography>{row.original.statusCode}</Typography>,
  },
  filter: {
    Component: (props) => (
      <FreeTextFilter {...props} placeholder={'Search by code'} />
    ),
  },
};

const getEntityByType = (
  entities: AuditLogEntitiesInner[]
): AuditLogEntitiesInner | undefined => {
  return entities
    .filter((x) => x.entityType && SupportedEntityTypes.includes(x.entityType))
    .sort((a, b) => {
      const aIndex = SupportedEntityTypes.indexOf(a.entityType || '');
      const bIndex = SupportedEntityTypes.indexOf(b.entityType || '');
      return aIndex - bIndex;
    })[0];
};

export const entityName: FilterProperty<AuditLog> = {
  name: 'entityName',
  displayName: 'Entity name',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) => {
      const entity = getEntityByType(row.original.entities || []);
      const name = entity?.displayName;
      return name && <Typography>{name}</Typography>;
    },
  },
};

export const entityType: FilterProperty<AuditLog> = {
  name: 'entityType',
  displayName: 'Entity type',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) => {
      const entity = getEntityByType(row.original.entities || []);
      const type = entity?.entityType;
      return type && <Typography>{type}</Typography>;
    },
  },
};

export const auditLogId: FilterProperty<AuditLog> = {
  name: 'id',
  displayName: 'Log ID',
  type: PropertyType.String,
  group: '',
  column: {
    enableSorting: false,
    cell: ({ row }) => <Typography>{row.original.id}</Typography>,
  },
  filter: {
    Component: (props) => (
      <FreeTextFilter {...props} placeholder={'Search by ID'} />
    ),
  },
};

export const auditLogPath: FilterProperty<AuditLog> = {
  name: 'path',
  displayName: 'Request path',
  type: PropertyType.String,
  group: '',
  column: {
    enableSorting: false,
    cell: ({ row }) => <Typography>{row.original.path}</Typography>,
  },
  filter: {
    Component: (props) => (
      <FreeTextFilter {...props} placeholder={'Search path'} />
    ),
  },
};

export const sourceIpAddress: FilterProperty<AuditLog> = {
  name: 'sourceIpAddress',
  displayName: 'Source IP',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) => <Typography>{row.original.sourceIpAddress}</Typography>,
  },
};

export const initiatorRole: FilterProperty<AuditLog> = {
  name: 'identityRole',
  displayName: 'Role',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) => <Typography>{row.original.initiatorRole}</Typography>,
  },
};

export const jobTemporalLink: FilterProperty<BackupJob | RestoreJob | ScanJob> =
  {
    name: 'temporalLink',
    displayName: 'Temporal link',
    type: PropertyType.String,
    group: '',
    field: {
      Component: ({ entity }) => {
        const dal = useDAL();
        const { isEmployee } = useUser();
        const { isDev, isDemo } = useEnvironment();
        const { data: prefResponse } = dal.preferences.useUserPref(
          PreferencesKey.Preferences
        );
        const showTemporalLink =
          isDev || (isEmployee && !isDemo && prefResponse?.value?.isDevMode);

        if (!entity.jobExecutionDetails.jobId || !showTemporalLink) {
          return null;
        }
        return <TemporalLinkField entity={entity} />;
      },
    },
  };

export const jobId: FilterProperty<BackupJob | RestoreJob | ScanJob> = {
  name: 'jobId',
  displayName: 'Job ID',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      row.original.jobExecutionDetails.jobId && (
        <CellActionsWrapper textToCopy={row.original.jobExecutionDetails.jobId}>
          <Typography
            sx={{
              lineHeight: '24px',
            }}
          >
            {row.original.jobExecutionDetails.jobId}
          </Typography>
        </CellActionsWrapper>
      ),
  },
  field: {
    Component: ({ entity }) => {
      const id = entity.jobExecutionDetails.jobId;
      return (
        id && (
          <CopyToClipboardWrapper textToCopy={id}>
            <Stack
              sx={{
                overflow: 'hidden',
                maxWidth: '530px',
              }}
            >
              <OverflowTooltip
                text={id}
                renderContent={(text, ref) => (
                  <Typography
                    className='truncate'
                    ref={ref}
                    sx={{
                      lineHeight: '24px',
                    }}
                  >
                    {text}
                  </Typography>
                )}
              />
            </Stack>
          </CopyToClipboardWrapper>
        )
      );
    },
  },
  filter: {
    Component: (props) => (
      <FreeTextFilter {...props} placeholder={'Search by ID'} />
    ),
  },
};

const baseJobErrorCode: FilterProperty<BackupJob | RestoreJob | ScanJob> = {
  name: 'errorCode',
  displayName: 'Error type',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) => {
      const errorCode = row.original.jobExecutionDetails.errorCode;
      if (!errorCode) {
        return null;
      }

      const errorTitle = ErrorCodes[errorCode]?.title || errorCode;
      return (
        <CellActionsWrapper textToCopy={errorTitle}>
          <Stack
            sx={{
              overflow: 'hidden',
              maxWidth: '280px',
            }}
          >
            <OverflowTooltip
              text={errorTitle}
              renderContent={(text, ref) => (
                <Typography
                  className='truncate'
                  ref={ref}
                  sx={{
                    lineHeight: '24px',
                  }}
                >
                  {text}
                </Typography>
              )}
            />
          </Stack>
        </CellActionsWrapper>
      );
    },
  },
  field: {
    Component: ({ entity }) => {
      const errorCode = entity.jobExecutionDetails.errorCode;
      if (!errorCode) {
        return null;
      }

      const errorTitle = ErrorCodes[errorCode]?.title || errorCode;
      return <Typography>{errorTitle}</Typography>;
    },
  },
  filter: {
    Badge({ conditionValue }) {
      return ErrorCodes[conditionValue]?.title || conditionValue;
    },
  },
};

export const backupJobErrorCode: FilterProperty<BackupJob> = {
  ...(baseJobErrorCode as FilterProperty<BackupJob>),
  filter: {
    ...baseJobErrorCode.filter,
    Component: (props) => {
      const dal = useDAL();
      const { data: body } = dal.jobs.backup.useListErrorCodes();
      const options =
        body?.errorCodes
          ?.map((code) => ({
            id: code,
            name: ErrorCodes[code]?.title || code,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)) || [];
      return <MultiSelectFilter {...props} options={options} />;
    },
  },
};

export const restoreJobErrorCode: FilterProperty<RestoreJob> = {
  ...(baseJobErrorCode as FilterProperty<RestoreJob>),
  filter: {
    ...baseJobErrorCode.filter,
    Component: (props) => {
      const dal = useDAL();
      const { data: body } = dal.jobs.restore.useListErrorCodes();
      const options =
        body?.errorCodes
          ?.map((code) => ({
            id: code,
            name: ErrorCodes[code]?.title || code,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)) || [];
      return <MultiSelectFilter {...props} options={options} />;
    },
  },
};

export const scanJobErrorCode: FilterProperty<ScanJob> = {
  ...(baseJobErrorCode as FilterProperty<ScanJob>),
  filter: {
    ...baseJobErrorCode.filter,
    Component: (props) => {
      const dal = useDAL();
      const { data: body } = dal.jobs.scan.useListErrorCodes();
      const options =
        body?.errorCodes
          ?.map((code) => ({
            id: code,
            name: ErrorCodes[code]?.title || code,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)) || [];
      return <MultiSelectFilter {...props} options={options} />;
    },
  },
};

export const jobErrorDetails: FilterProperty<BackupJob | RestoreJob | ScanJob> =
  {
    name: 'statusMessage',
    displayName: 'Error details',
    type: PropertyType.String,
    group: '',
    column: {
      enableColumnFilter: false, // until filter is working
      enableSorting: false,
      cell: ({ row }) => {
        const status = row.original.jobExecutionDetails.statusMessage;
        if (!status) {
          return;
        }

        return (
          <CellActionsWrapper textToCopy={status}>
            <Stack
              sx={{
                overflow: 'hidden',
                maxWidth: '280px',
              }}
            >
              <OverflowTooltip
                text={status}
                renderContent={(text, ref) => (
                  <Typography
                    className='truncate'
                    ref={ref}
                    sx={{
                      lineHeight: '24px',
                    }}
                  >
                    {text}
                  </Typography>
                )}
              />
            </Stack>
          </CellActionsWrapper>
        );
      },
    },
    filter: {
      Component: (props) => (
        <FreeTextFilter {...props} placeholder={'Search by message'} />
      ),
    },
  };

export const jobStatus: FilterProperty<BackupJob | RestoreJob | ScanJob> = {
  name: 'status',
  displayName: 'Job status',
  type: PropertyType.String,
  group: '',
  column: {
    size: 10,
    minSize: 10,
    maxSize: 10,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const status = row.original.jobExecutionDetails.status;
      return status && <JobStatusIcon jobStatus={status} />;
    },
  },
  filter: {
    Component: (props) => {
      const options = Object.entries(JobStatuses)
        .filter(([key]) => key !== JobStatus.JOB_UNSPECIFIED)
        .map(([key, value]) => ({
          id: key,
          name: value.title,
          Image: (
            <JobStatusIcon
              hideTooltip={true}
              isStatic={true}
              jobStatus={key as JobStatus}
            />
          ),
        }));

      return <MultiSelectFilter {...props} options={options} />;
    },
    Badge({ conditionValue }) {
      return JobStatuses[conditionValue]?.title || conditionValue;
    },
  },
};

export const jobStatusWithoutCancel: FilterProperty<
  BackupJob | RestoreJob | ScanJob
> = {
  ...jobStatus,
  filter: {
    ...jobStatus.filter,
    Component: (props) => {
      const options = Object.entries(JobStatuses)
        .filter(
          ([key]) =>
            key !== JobStatus.JOB_UNSPECIFIED && key !== JobStatus.JOB_CANCELED
        )
        .map(([key, value]) => ({
          id: key,
          name: value.title,
          Image: (
            <JobStatusIcon
              hideTooltip={true}
              isStatic={true}
              jobStatus={key as JobStatus}
            />
          ),
        }));

      return <MultiSelectFilter {...props} options={options} />;
    },
  },
};

export const startTime: FilterProperty<BackupJob | RestoreJob | ScanJob> = {
  name: 'startTime',
  displayName: 'Start time (UTC)',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      isDateValid(row.original.jobExecutionDetails.startTime) && (
        <Typography>
          {dayjs
            .utc(row.original.jobExecutionDetails.startTime)
            .format(DATE_TIME_FORMAT)}
        </Typography>
      ),
  },
  filter: {
    shouldHideBadge: () => true,
  },
};

export const creationTime: FilterProperty<BackupJob | RestoreJob | ScanJob> = {
  name: 'createdTime',
  displayName: 'Creation time (UTC)',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      isDateValid(row.original.jobExecutionDetails.createdTime) && (
        <Typography>
          {dayjs
            .utc(row.original.jobExecutionDetails.createdTime)
            .format(DATE_TIME_FORMAT)}
        </Typography>
      ),
  },
  filter: {
    shouldHideBadge: () => true,
  },
};

export const expectedStartTime: FilterProperty<
  BackupJob | RestoreJob | ScanJob
> = {
  name: 'expectedStartTime',
  displayName: 'Expected start time (UTC)',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      isDateValid(row.original.jobExecutionDetails.expectedStartTime) && (
        <Typography>
          {dayjs
            .utc(row.original.jobExecutionDetails.expectedStartTime)
            .format(DATE_TIME_FORMAT)}
        </Typography>
      ),
  },
  filter: {
    shouldHideBadge: () => true,
  },
};

const Initiator = ({
  entity,
}: {
  entity: BackupJob | RestoreJob | AuditLog;
}) => {
  const dal = useDAL();
  const { data: userBody } = dal.users.useList();
  const { data: apiCredentialsBody } = dal.apiCredentials.useList();
  const { data: backupPoliciesBody } = dal.backupPolicy.useList();

  if ('backupPolicyId' in entity) {
    const backupPolicy = backupPoliciesBody?.backupPolicies.find(
      (x) => x.id === entity.backupPolicyId
    );
    if (backupPolicy) {
      return <BackupPolicyTag policy={backupPolicy} />;
    }
  }

  if (isJob(entity) && !entity.jobExecutionDetails.isOnDemand) {
    return <Typography>Scheduled</Typography>;
  }

  const values = [
    ...(userBody?.users || []),
    ...(apiCredentialsBody?.apiCredentials || []),
  ];

  const initiatorId = isJob(entity)
    ? entity.jobExecutionDetails.onDemandInitiator?.id
    : entity.initiatorId;

  if (!initiatorId) {
    return null;
  }

  const initiator = values.find((a) => a.id === initiatorId);
  let displayName = getUserName(initiator);
  if (!displayName && !isJob(entity) && entity.initiatorName) {
    displayName = entity.initiatorName;
  }

  return (
    <Stack
      direction='row'
      sx={{
        alignItems: 'center',
        gap: '12px',
      }}
    >
      {displayName && (
        <UserCircle
          text={!isJob(entity) ? entity.initiatorName : undefined}
          user={initiator}
        />
      )}
      <Typography>{displayName || initiatorId}</Typography>
    </Stack>
  );
};

const baseInitiatorId: FilterProperty<BackupJob | RestoreJob | AuditLog> = {
  name: 'initiatorId',
  displayName: 'Initiated by',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }: { row: Row<BackupJob | RestoreJob | AuditLog> }) => {
      return <Initiator entity={row.original} />;
    },
  },
  field: {
    Component: ({ entity }) => <Initiator entity={entity} />,
  },
};

export const backupJobInitiatorId: FilterProperty<BackupJob> = {
  ...(baseInitiatorId as FilterProperty<BackupJob>),
  filter: {
    fullPanelComponent: {
      title: 'Initiated by',
      subtitle:
        'Choose one or more triggers to show only the jobs they started.	',
      Component: (props) => {
        const dal = useDAL();
        const { data: userBody, isLoading: isLoadingUsers } =
          dal.users.useList();
        const { data: apiCredentialsBody, isLoading: isLoadingApiCredentials } =
          dal.apiCredentials.useList();
        const { data: backupPoliciesBody, isLoading: isLoadingBackupPolicies } =
          dal.backupPolicy.useList();

        const backupPolicies =
          backupPoliciesBody?.backupPolicies
            .sort((a) => (a.enabled ? -1 : 1))
            .map((b) => ({
              id: b.id,
              name: b.name,
              hideName: true,
              Image: <BackupPolicyTag policy={b} disableTooltip={true} />,
            })) || [];

        const users =
          userBody?.users
            .map((u) => ({
              id: u.id,
              name: u.givenName,
              Image: <UserCircle user={u} />,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)) || [];

        const apiCredentials =
          apiCredentialsBody?.apiCredentials
            .map((a) => ({
              id: a.id,
              name: a.name,
              Image: <UserCircle user={a} />,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)) || [];

        const options = [
          ...(backupPolicies.length
            ? [
                {
                  id: 'Backup policies',
                  name: 'Backup policies',
                  isGroupTitle: true,
                },
                ...backupPolicies,
              ]
            : []),
          ...(users.length
            ? [
                {
                  id: 'Users',
                  name: 'Users',
                  isGroupTitle: true,
                },
                ...users,
              ]
            : []),
          ...(apiCredentials.length
            ? [
                {
                  id: 'API credentials',
                  name: 'API credentials',
                  isGroupTitle: true,
                },
                ...apiCredentials,
              ]
            : []),
        ];

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='initiator'
            isLoading={
              isLoadingUsers ||
              isLoadingApiCredentials ||
              isLoadingBackupPolicies
            }
            options={options}
          />
        );
      },
    },
    Badge: ({ conditionValue }) => {
      const dal = useDAL();
      const { data: userBody } = dal.users.useList();
      const { data: apiCredentialsBody } = dal.apiCredentials.useList();
      const { data: backupPoliciesBody } = dal.backupPolicy.useList();

      const policy = backupPoliciesBody?.backupPolicies.find(
        (x) => x.id === conditionValue
      );
      if (policy) {
        return policy.name || conditionValue;
      }

      const values = [
        ...(userBody?.users || []),
        ...(apiCredentialsBody?.apiCredentials || []),
      ];

      const found = values.find((x) => x.id === conditionValue);
      const badgeName = getUserName(found);
      return badgeName || conditionValue;
    },
  },
};

export const restoreJobInitiatorId: FilterProperty<RestoreJob> = {
  ...(baseInitiatorId as FilterProperty<RestoreJob>),
  filter: {
    fullPanelComponent: {
      title: 'Initiated by',
      subtitle:
        'Choose one or more triggers to show only the jobs they started.	',
      Component: (props) => {
        const dal = useDAL();
        const { data: userBody, isLoading: isLoadingUsers } =
          dal.users.useList();
        const { data: apiCredentialsBody, isLoading: isLoadingApiCredentials } =
          dal.apiCredentials.useList();

        const users =
          userBody?.users
            .map((u) => ({
              id: u.id,
              name: u.givenName,
              Image: <UserCircle user={u} />,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)) || [];

        const apiCredentials =
          apiCredentialsBody?.apiCredentials
            .map((a) => ({
              id: a.id,
              name: a.name,
              Image: <UserCircle user={a} />,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)) || [];

        const options = [
          ...(users.length
            ? [
                {
                  id: 'Users',
                  name: 'Users',
                  isGroupTitle: true,
                },
                ...users,
              ]
            : []),
          ...(apiCredentials.length
            ? [
                {
                  id: 'API credentials',
                  name: 'API credentials',
                  isGroupTitle: true,
                },
                ...apiCredentials,
              ]
            : []),
        ];

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='initiator'
            isLoading={isLoadingUsers || isLoadingApiCredentials}
            options={options}
          />
        );
      },
    },
    Badge: ({ conditionValue }) => {
      const dal = useDAL();
      const { data: userBody } = dal.users.useList();
      const { data: apiCredentialsBody } = dal.apiCredentials.useList();

      const values = [
        ...(userBody?.users || []),
        ...(apiCredentialsBody?.apiCredentials || []),
      ];

      const found = values.find((x) => x.id === conditionValue);
      const badgeName = getUserName(found);
      return badgeName || conditionValue;
    },
  },
};

export const auditLogInitiatorId: FilterProperty<AuditLog> = {
  ...(baseInitiatorId as FilterProperty<AuditLog>),
  group: '',
  name: 'userId',
  filter: {
    fullPanelComponent: {
      title: 'Initiated by',
      subtitle:
        'Filter for operations carried out by specific users or API clients.',
      Component: (props) => {
        const dal = useDAL();
        const { data: userBody, isLoading: isLoadingUsers } =
          dal.users.useList();
        const { data: apiCredentialsBody, isLoading: isLoadingApiCredentials } =
          dal.apiCredentials.useList();

        const users =
          userBody?.users
            .map((u) => ({
              id: u.id,
              name: u.givenName,
              Image: <UserCircle user={u} />,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)) || [];

        const apiCredentials =
          apiCredentialsBody?.apiCredentials
            .map((a) => ({
              id: a.id,
              name: a.name,
              Image: <UserCircle user={a} />,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)) || [];

        const options = [
          ...(users.length
            ? [
                {
                  id: 'Users',
                  name: 'Users',
                  isGroupTitle: true,
                },
                ...users,
              ]
            : []),
          ...(apiCredentials.length
            ? [
                {
                  id: 'API credentials',
                  name: 'API credentials',
                  isGroupTitle: true,
                },
                ...apiCredentials,
              ]
            : []),
        ];

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='initiator'
            isLoading={isLoadingUsers || isLoadingApiCredentials}
            options={options}
          />
        );
      },
    },
    Badge: ({ conditionValue }) => {
      const dal = useDAL();
      const { data: userBody } = dal.users.useList();
      const { data: apiCredentialsBody } = dal.apiCredentials.useList();

      const values = [
        ...(userBody?.users || []),
        ...(apiCredentialsBody?.apiCredentials || []),
      ];

      const found = values.find((x) => x.id === conditionValue);
      const badgeName = getUserName(found);
      return badgeName || conditionValue;
    },
  },
};

export const restoreAccountName: FilterProperty<RestoreJob> = {
  name: 'accountDisplayName',
  displayName: 'Restore account name',
  type: PropertyType.SingleValue,
  group: 'restore-to',
  policyRelation: PropertyPolicyRelation.SingleValue,
  column: {
    enableSorting: false,
    cell: ({ row }: { row: Row<RestoreJob> }) => (
      <JobRestoreAccountIndicator job={row.original} showAccountId={false} />
    ),
  },
  filter: {
    Component: (props) => {
      return <FreeTextFilter {...props} placeholder='Search by name' />;
    },
  },
};

export const restoreAccountId: FilterProperty<RestoreJob> = {
  name: 'restoreAccount',
  displayName: 'Restore account ID',
  type: PropertyType.String,
  group: 'restore-to',
  column: {
    cell: ({ row }: { row: Row<RestoreJob> }) => {
      return CloudAccountCell(row.original);
    },
  },
  field: {
    Component: ({ entity }) => {
      if (!entity.jobExecutionDetails.onDemandInitiator?.id) {
        return null;
      }

      return (
        <JobRestoreAccountIndicator job={entity} showCloudIndicator={false} />
      );
    },
  },
  filter: {
    fullPanelComponent: {
      title: 'Restore account',
      subtitle: 'To filter your resources, select one or more restore accounts',
      Component: (props) => {
        const { data: body, isLoading } = useRestoreAccounts();

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='account'
            isLoading={isLoading}
            shouldShowId
            options={(body?.accounts || []).map((account) => {
              const cloudProvider =
                account.restoreAccountAttributes.cloudProvider;
              return {
                id: account.providerAccountId,
                name: account.name,
                Image: CloudProviders[cloudProvider] && (
                  <CircleImage
                    alt={CloudProviders[cloudProvider].displayName}
                    src={CloudProviders[cloudProvider].logo}
                    className='my-[16px]'
                  />
                ),
              };
            })}
          />
        );
      },
    },
    Badge: ({ conditionValue }) => {
      const { data: body, isLoading } = useRestoreAccounts();

      if (isLoading) {
        return '...';
      }

      return (
        body?.accounts.find((x) => x.providerAccountId === conditionValue)
          ?.name || conditionValue
      );
    },
  },
};

export const restoreRegion: FilterProperty<RestoreJob> = {
  name: 'restoreRegion',
  displayName: 'Restore region',
  type: PropertyType.String,
  group: 'restore-to',
  column: {
    // enableColumnFilter: false,
    cell: ({ row }) =>
      row.original.destinationDetails.region && (
        <Typography>{row.original.destinationDetails.region}</Typography>
      ),
  },
  field: {
    Component: ({ entity }) => {
      return (
        entity.destinationDetails.region && (
          <Typography>{entity.destinationDetails.region}</Typography>
        )
      );
    },
  },
  filter: {
    Component: (props) => {
      const dal = useDAL();
      const { data: accountsBody } = useRestoreAccounts();
      const { data: regionsBody } = dal.restore.regions.useListMany(
        accountsBody?.accounts || []
      );

      return (
        <MultiSelectFilter
          {...props}
          options={(regionsBody?.regions || [])
            .map((x) => ({ id: x, name: x }))
            .sort((a, b) => a.name.localeCompare(b.name))}
        />
      );
    },
  },
};

export const endTime: FilterProperty<BackupJob | RestoreJob | ScanJob> = {
  name: 'endTime',
  displayName: 'End time (UTC)',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      isDateValid(row.original.jobExecutionDetails.endTime) && (
        <Typography>
          {dayjs
            .utc(row.original.jobExecutionDetails.endTime)
            .format(DATE_TIME_FORMAT)}
        </Typography>
      ),
  },
  filter: {
    shouldHideBadge: () => true,
  },
};

export const vault: FilterProperty<BackupJob | RestoreJob> = {
  name: 'vaultId',
  displayName: 'Vault',
  type: PropertyType.String,
  group: 'backup-to',
  column: {
    cell: ({ row }) =>
      row.original.vault && <VaultTag vault={row.original.vault.id} />,
  },
  field: {
    Component: ({ entity }) => {
      return entity.vault && <VaultTag vault={entity.vault.id} />;
    },
  },
  filter: {
    Component: (props) => {
      const { vaults } = useBackupVaults();

      return <MultiSelectFilter {...props} options={getVaultOptions(vaults)} />;
    },
    fullPanelComponent: {
      title: 'Vault',
      subtitle: 'To filter your jobs, select one or more vaults',
      useShouldUseFullPanel: () => {
        const { vaults } = useBackupVaults();
        return vaults.length > INLINE_FILTER_ITEMS_LIMIT;
      },
      Component: (props) => {
        const { vaults, isLoading } = useBackupVaults();

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='vault'
            isLoading={isLoading}
            options={getVaultOptions(vaults)}
          />
        );
      },
    },
    Badge: ({ conditionValue }) => {
      const { vaults } = useBackupVaults();

      return vaults.find((x) => x.id === conditionValue)?.name || '...';
    },
  },
};

export const dbEngine: FilterProperty<InventoryResource> = {
  name: 'dbEngine',
  displayName: 'Engine',
  type: PropertyType.String,
  group: 'primary',
  field: {
    Component: ({ entity }) => {
      const engine =
        entity.resourceProperties?.awsRds?.engine ||
        entity.resourceProperties?.gcpCloudSql?.engine;
      const engineVersion =
        entity.resourceProperties?.awsRds?.engineVersion ||
        entity.resourceProperties?.gcpCloudSql?.engineVersion;
      return (
        engine && (
          <Box className='flex items-center'>
            <CircleImage
              alt={engine}
              src={ApplicationsLogosWithAliases[engine]}
              className='mr-[8px]'
            />
            <Typography className='capitalize'>
              {(DatabaseEngineAliases[engine] ?? engine) +
                (engineVersion ? ` (${engineVersion})` : '')}
            </Typography>
          </Box>
        )
      );
    },
  },
};

export const timestamp: FilterProperty<Notification> = {
  name: 'timestamp',
  displayName: 'Timestamp (UTC)',
  type: PropertyType.String,
  group: 'primary',
  column: {
    cell: ({ row }) =>
      row.original.timestamp && (
        <Typography>
          {dayjs.utc(row.original.timestamp).format(DATE_TIME_FORMAT)}
        </Typography>
      ),
  },
};

export const notificationType: FilterProperty<Notification> = {
  name: 'type',
  displayName: 'Type',
  type: PropertyType.String,
  group: 'primary',
  column: {
    cell: ({ row }) =>
      row.original.type && (
        <Typography>{NotificationTypes[row.original.type]?.title}</Typography>
      ),
  },
  filter: {
    Component: (props) => {
      return (
        <MultiSelectFilter
          {...props}
          options={supportedNotificationTypes.map((id) => ({
            id,
            name: NotificationTypes[id]?.title,
          }))}
        />
      );
    },
    Badge: ({ conditionValue }) => {
      return NotificationTypes[conditionValue]?.title;
    },
  },
};

export const notificationStatus: FilterProperty<Notification> = {
  name: 'status',
  displayName: 'Status',
  type: PropertyType.String,
  group: 'primary',
  column: {
    cell: ({ row }) =>
      row.original.status && (
        <Typography>
          {NotificationStatuses[row.original.status]?.title}
        </Typography>
      ),
  },
  filter: {
    Component: (props) => (
      <MultiSelectFilter
        {...props}
        options={Object.values(NotificationStatus)
          .sort((a, b) => a.localeCompare(b))
          .map((id) => ({
            id,
            name: NotificationStatuses[id]?.title,
          }))}
      />
    ),
    Badge: ({ conditionValue }) => {
      return NotificationStatuses[conditionValue]?.title;
    },
  },
};

export const notificationPolicyName: FilterProperty<Notification> = {
  name: 'policyId',
  displayName: 'Policy Name',
  type: PropertyType.String,
  group: 'primary',
  column: {
    cell: ({ row }) =>
      row.original.policyName && (
        <Typography>{row.original.policyName}</Typography>
      ),
  },
  filter: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body } = dal.notifications.policies.useList();

      return (
        <MultiSelectFilter
          {...props}
          options={(body?.notificationPolicies || []).map((a) => ({
            id: a.id,
            name: a.name,
          }))}
        />
      );
    },
    Badge: ({ conditionValue }) => {
      const dal = useDAL();
      const { data: body } = dal.notifications.policies.useList();

      return (
        body?.notificationPolicies.find((x) => x.id === conditionValue)?.name ||
        '...'
      );
    },
  },
};

export const accountDisplayName: FilterProperty<Account> = {
  name: 'accountName',
  displayName: 'Display name',
  type: PropertyType.SingleValue,
  policyRelation: PropertyPolicyRelation.SingleValue,
  group: 'primary',
  column: {
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => (
      <CloudProviderIndicator
        cloudProvider={row.original.cloudProvider}
        text={row.original.name}
      />
    ),
  },
  filter: {
    Component: (props) => {
      return (
        <FreeTextFilter
          {...props}
          showSearchIcon={false}
          placeholder='Search for display name'
        />
      );
    },
  },
};

export const accountFreeTextProperty: FilterProperty<Account> = {
  name: 'freeText',
  displayName: 'Account free text',
  type: PropertyType.SingleValue,
  group: 'primary',
  filter: {
    Component: (props) => {
      return <FreeTextFilter {...props} placeholder='Search by name or ID' />;
    },
  },
};

export const providerAccountId: FilterProperty<EksCluster | Account> = {
  name: 'providerAccountId',
  displayName: 'Account ID',
  type: PropertyType.SingleValue,
  policyRelation: PropertyPolicyRelation.SingleValue,
  group: 'primary',
  column: {
    enableSorting: false,
    cell: ({ row }) =>
      row.original.providerAccountId && (
        <Typography>{row.original.providerAccountId}</Typography>
      ),
  },
  condition: {
    Component: (props) => {
      const { sourceAccounts } = useSourceAccounts();

      return (
        <AccountCondition
          {...props}
          showNameFirst={true}
          accounts={(sourceAccounts ?? []).filter(
            (x) =>
              !props.supportedProviders?.length ||
              props.supportedProviders?.includes(
                x.sourceAccountAttributes.cloudProvider
              )
          )}
        />
      );
    },
  },
};

export const awsRestoreAccountId: FilterProperty<Account> = {
  ...(providerAccountId as FilterProperty<Account>),
  displayName: 'Account',
  condition: {
    Component: (props) => {
      const { data: restoreAccountsBody, isLoading } = useRestoreAccounts(
        createCloudProviderCondition([Provider.Aws])
      );

      return (
        <AccountCondition
          {...props}
          isLoading={isLoading}
          showNameFirst={true}
          accounts={restoreAccountsBody?.accounts ?? []}
        />
      );
    },
  },
};

export const awsScanningAccountId: FilterProperty<Account> = {
  ...(providerAccountId as FilterProperty<Account>),
  displayName: 'Account',
  condition: {
    Component: (props) => {
      const dal = useDAL();
      const { data, isLoading } = dal.cloudAccounts.scanning.useList();
      const awsAccounts =
        data?.accounts.filter(
          (account) =>
            account.isCustomerOwned && account.cloudProvider === Provider.Aws
        ) || [];

      return (
        <AccountCondition
          {...props}
          isLoading={isLoading}
          showNameFirst={true}
          accounts={awsAccounts}
        />
      );
    },
  },
};

export const awsVaultAccountId: FilterProperty<Account> = {
  ...(providerAccountId as FilterProperty<Account>),
  displayName: 'Account',
  condition: {
    Component: (props) => {
      const dal = useDAL();
      const { data, isLoading } = dal.cloudAccounts.vault.useList();
      const awsAccounts =
        data?.accounts.filter(
          (account) =>
            account.isCustomerOwned && account.cloudProvider === Provider.Aws
        ) || [];

      return (
        <AccountCondition
          {...props}
          isLoading={isLoading}
          showNameFirst={true}
          accounts={awsAccounts}
        />
      );
    },
  },
};

export const azureRestoreSubscriptionId: FilterProperty<Account> = {
  ...(providerAccountId as FilterProperty<Account>),
  displayName: 'Subscription',
  condition: {
    Component: (props) => {
      const { data: restoreAccountsBody, isLoading } = useRestoreAccounts(
        createCloudProviderCondition([Provider.Azure])
      );

      return (
        <AccountCondition
          {...props}
          isLoading={isLoading}
          showNameFirst={true}
          accounts={restoreAccountsBody?.accounts ?? []}
        />
      );
    },
  },
};

export const azureScanningSubscriptionId: FilterProperty<Account> = {
  ...(providerAccountId as FilterProperty<Account>),
  displayName: 'Subscription',
  condition: {
    Component: (props) => {
      const dal = useDAL();
      const { data, isLoading } = dal.cloudAccounts.scanning.useList();
      const azureAccounts =
        data?.accounts.filter(
          (account) =>
            account.isCustomerOwned && account.cloudProvider === Provider.Azure
        ) || [];

      return (
        <AccountCondition
          {...props}
          isLoading={isLoading}
          showNameFirst={true}
          accounts={azureAccounts}
        />
      );
    },
  },
};

export const azureVaultSubscriptionId: FilterProperty<Account> = {
  ...(providerAccountId as FilterProperty<Account>),
  displayName: 'Subscription',
  condition: {
    Component: (props) => {
      const dal = useDAL();
      const { data, isLoading } = dal.cloudAccounts.vault.useList();
      const azureAccounts =
        data?.accounts.filter(
          (account) =>
            account.isCustomerOwned && account.cloudProvider === Provider.Azure
        ) || [];

      return (
        <AccountCondition
          {...props}
          isLoading={isLoading}
          showNameFirst={true}
          accounts={azureAccounts}
        />
      );
    },
  },
};

export const gcpRestoreProjectId: FilterProperty<Account> = {
  ...(providerAccountId as FilterProperty<Account>),
  displayName: 'Project',
  condition: {
    Component: (props) => {
      const { data: restoreAccountsBody, isLoading } = useRestoreAccounts(
        createCloudProviderCondition([Provider.Gcp])
      );

      return (
        <AccountCondition
          {...props}
          isLoading={isLoading}
          showNameFirst={true}
          accounts={restoreAccountsBody?.accounts ?? []}
        />
      );
    },
  },
};

export const gcpScanningProjectId: FilterProperty<Account> = {
  ...(providerAccountId as FilterProperty<Account>),
  displayName: 'Project',
  condition: {
    Component: (props) => {
      const dal = useDAL();
      const { data, isLoading } = dal.cloudAccounts.scanning.useList();
      const gcpAccounts =
        data?.accounts.filter(
          (account) =>
            account.isCustomerOwned && account.cloudProvider === Provider.Gcp
        ) || [];

      return (
        <AccountCondition
          {...props}
          isLoading={isLoading}
          showNameFirst={true}
          accounts={gcpAccounts}
        />
      );
    },
  },
};

export const gcpVaultProjectId: FilterProperty<Account> = {
  ...(providerAccountId as FilterProperty<Account>),
  displayName: 'Project',
  condition: {
    Component: (props) => {
      const dal = useDAL();
      const { data, isLoading } = dal.cloudAccounts.vault.useList();
      const gcpAccounts =
        data?.accounts.filter(
          (account) =>
            account.isCustomerOwned && account.cloudProvider === Provider.Gcp
        ) || [];

      return (
        <AccountCondition
          {...props}
          isLoading={isLoading}
          showNameFirst={true}
          accounts={gcpAccounts}
        />
      );
    },
  },
};

export const accountState: FilterProperty<
  Account | SourceAwsOrganizationalUnit
> = {
  name: 'accountStatus',
  displayName: 'Connection status',
  type: PropertyType.String,
  policyRelation: PropertyPolicyRelation.SingleValue,
  group: 'primary',
  column: {
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const stateValue =
        'status' in row.original ? row.original.status : row.original.state;
      return (
        <Typography>{AccountStatuses[stateValue || '']?.title}</Typography>
      );
    },
  },
  condition: {
    Component: (props) => {
      return (
        <MultiSelectConditionPanel
          {...props}
          entityName='status'
          isLoading={false}
          showAddNewButton={false}
          showSearchBox={false}
          options={Object.entries(AccountStatuses)
            .filter(([key]) => key !== AccountState.ACCOUNT_STATE_UNSPECIFIED)
            .map(([key, value]) => ({
              id: key,
              name: value.title,
            }))}
        />
      );
    },
  },
};

export const installedVersion: FilterProperty<Account> = {
  name: 'installedVersion',
  displayName: 'Permissions version',
  type: PropertyType.String,
  group: 'primary',
  column: {
    enableSorting: false,
    cell: ({ row }) => <EonRoleVersion account={row.original} />,
  },
};

export const awsOrganizationUnit: FilterProperty<Account> = {
  name: 'providerParentId',
  displayName: 'Organizational unit',
  type: PropertyType.SingleValue,
  policyRelation: PropertyPolicyRelation.SingleValue,
  group: 'primary',
  condition: {
    Component: (props) => {
      const dal = useDAL();
      const { data: organizationsBody, isLoading } =
        dal.cloudAccounts.sourceAwsOrganizationalUnit.useList();

      return (
        <MultiSelectConditionPanel
          {...props}
          showAddNewButton={false}
          showSearchBox={true}
          isLoading={isLoading}
          entityName='organizational unit'
          shouldShowId
          showNameFirst
          options={(organizationsBody?.organizationalUnits ?? []).map(
            (organization) => ({
              id: organization.providerOrganizationalUnitId,
              name:
                organization.name === organization.providerManagementAccountId
                  ? organization.providerOrganizationalUnitId
                  : organization.name,
            })
          )}
        />
      );
    },
  },
};

export const gcpOrgAndFolders: FilterProperty<Account> = {
  name: 'providerParentId',
  displayName: 'Organizations or folder',
  type: PropertyType.SingleValue,
  policyRelation: PropertyPolicyRelation.SingleValue,
  group: 'primary',
  condition: {
    Component: (props) => {
      const { orgsAndFolders, isLoading } = useOrgsAndFolders();

      return (
        <MultiSelectConditionPanel
          {...props}
          showAddNewButton={false}
          showSearchBox={true}
          isLoading={isLoading}
          entityName='organizations or folder'
          shouldShowId
          showNameFirst
          options={orgsAndFolders.map((organization) => {
            const id = organization.folderId ?? organization.organizationId;
            return {
              id,
              name:
                organization.name === organization.managementProjectId
                  ? id
                  : organization.name,
            };
          })}
        />
      );
    },
  },
};

export const gcpRestoreOrgAndFolders: FilterProperty<Account> = {
  name: 'providerParentId',
  displayName: 'Organizations or folder',
  type: PropertyType.SingleValue,
  policyRelation: PropertyPolicyRelation.SingleValue,
  group: 'primary',
  condition: {
    Component: (props) => {
      const { orgsAndFolders, isLoading } = useRestoreOrgsAndFolders();

      return (
        <MultiSelectConditionPanel
          {...props}
          showAddNewButton={false}
          showSearchBox={true}
          isLoading={isLoading}
          entityName='organizations or folder'
          shouldShowId
          showNameFirst
          options={orgsAndFolders.map((organization) => {
            const id = organization.folderId ?? organization.organizationId;
            return {
              id,
              name:
                organization.name === organization.managementProjectId
                  ? id
                  : organization.name,
            };
          })}
        />
      );
    },
  },
};

export const azureManagementGroup: FilterProperty<Account> = {
  name: 'providerParentId',
  displayName: 'Management group',
  type: PropertyType.SingleValue,
  policyRelation: PropertyPolicyRelation.SingleValue,
  group: 'primary',
  condition: {
    Component: (props) => {
      const { managementGroups, isLoading } = useManagementGroups();

      return (
        <MultiSelectConditionPanel
          {...props}
          showAddNewButton={false}
          showSearchBox={true}
          isLoading={isLoading}
          entityName='management group'
          shouldShowId
          showNameFirst
          options={managementGroups.map((group) => ({
            id: group.managementGroupId || group.id,
            name: group.name || group.managementGroupId || group.id,
          }))}
        />
      );
    },
  },
};

export const region: FilterProperty<EksCluster> = {
  name: 'region',
  displayName: 'Region',
  type: PropertyType.SingleValue,
  group: 'primary',
  column: {
    enableSorting: false,
    cell: ({ row }) =>
      row.original.region && <Typography>{row.original.region}</Typography>,
  },
};

export const eksClusterStatus: FilterProperty<EksCluster> = {
  name: 'status',
  displayName: 'Status',
  type: PropertyType.SingleValue,
  group: 'primary',
  column: {
    enableSorting: false,
    cell: ({ row }) =>
      row.original.status && (
        <Typography>
          {EksClusterStatuses[row.original.status]?.title}
        </Typography>
      ),
  },
};

export const kubeAccessStatus: FilterProperty<EksCluster> = {
  name: 'kubeAccessStatus',
  displayName: 'Kube Access Status',
  type: PropertyType.SingleValue,
  group: 'primary',
  column: {
    enableSorting: false,
    cell: ({ row }) =>
      row.original.kubeAccessStatus && (
        <Typography>
          {KubeAccessStatuses[row.original.kubeAccessStatus]?.title}
        </Typography>
      ),
  },
};

export const clusterName: FilterProperty<InventoryResource | EksCluster> = {
  name: 'clusterName',
  displayName: 'Cluster name',
  type: PropertyType.SingleValue,
  group: 'source-location',
  propertyPredicate: (providers) =>
    providers.includes(Provider.Aws) || providers.includes(Provider.Gcp),
  column: {
    enableSorting: true,
    cell: ({ row }) => {
      if ('clusterName' in row.original) {
        return <Typography>{row.original.clusterName}</Typography>;
      }

      const config =
        row.original.resourceProperties?.awsEksNamespace ||
        row.original.resourceProperties?.azureAksNamespace ||
        row.original.resourceProperties?.gcpGkeNamespace;

      return (
        !!config?.clusterName && <Typography>{config.clusterName}</Typography>
      );
    },
  },
  field: {
    Component: ({ entity }) => {
      if ('clusterName' in entity) {
        return null;
      }

      const config =
        entity.resourceProperties?.awsEksNamespace ||
        entity.resourceProperties?.azureAksNamespace ||
        entity.resourceProperties?.gcpGkeNamespace;

      return (
        config?.clusterName && <Typography>{config.clusterName}</Typography>
      );
    },
  },
  filter: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body } = dal.cloudAccounts.eksClusters.useList();

      return (
        <MultiSelectFilter
          {...props}
          options={(body?.eksClusters || []).map((x) => ({
            id: x.clusterName,
            name: x.clusterName,
          }))}
        />
      );
    },
  },
};

export const globalClusterIdentifier: FilterProperty<InventoryResource> = {
  name: 'globalClusterIdentifier',
  displayName: 'Global database name',
  type: PropertyType.SingleValue,
  group: 'source-location',
  policyRelation: PropertyPolicyRelation.SingleValue,
  propertyPredicate: (providers) => providers.includes(Provider.Aws),
  column: {
    enableSorting: true,
    cell: ({ row }) => {
      const globalClusterId =
        row.original.resourceProperties?.awsRds?.aurora
          ?.globalClusterIdentifier;
      return !!globalClusterId && <Typography>{globalClusterId}</Typography>;
    },
  },
  field: {
    Component: ({ entity }) => {
      const globalClusterId =
        entity.resourceProperties?.awsRds?.aurora?.globalClusterIdentifier;
      return globalClusterId && <Typography>{globalClusterId}</Typography>;
    },
  },
  filter: {
    fullPanelComponent: {
      title: 'Global database name',
      subtitle:
        'To filter your resources, select one or more Global database names',
      Component: (props) => {
        const dal = useDAL();
        const { data: body, isLoading } =
          dal.inventory.globalDatabases.useList();

        return (
          <FullPanelMultiSelectFilter
            {...props}
            entityName='global database'
            isLoading={isLoading}
            options={(body?.globalDatabases || []).map((x: string) => ({
              id: x,
              name: x,
            }))}
          />
        );
      },
    },
  },
  condition: {
    Component: (props) => {
      const dal = useDAL();
      const { data: body, isLoading } = dal.inventory.globalDatabases.useList();
      return (
        <MultiSelectConditionPanel
          showAddNewButton
          showSearchBox
          isLoading={isLoading}
          entityName='Global database name'
          options={(body?.globalDatabases || []).map((x: string) => ({
            id: x,
            name: x,
          }))}
          {...props}
        />
      );
    },
  },
};

export const namespaceName: FilterProperty<InventoryResource> = {
  name: 'namespace',
  displayName: 'Namespace',
  type: PropertyType.SingleValue,
  group: 'source-location',
  propertyPredicate: (providers) =>
    providers.includes(Provider.Aws) || providers.includes(Provider.Gcp),
  column: {
    enableSorting: false,
    cell: ({ row }) => {
      const config =
        row.original.resourceProperties?.awsEksNamespace ||
        row.original.resourceProperties?.azureAksNamespace ||
        row.original.resourceProperties?.gcpGkeNamespace;

      return !!config?.name && <Typography>{config.name}</Typography>;
    },
  },
  field: {
    Component: ({ entity }) => {
      const config =
        entity.resourceProperties?.awsEksNamespace ||
        entity.resourceProperties?.azureAksNamespace ||
        entity.resourceProperties?.gcpGkeNamespace;

      return config?.name && <Typography>{config.name}</Typography>;
    },
  },
};

export const s3BucketBackupType: FilterProperty<InventoryResource> = {
  name: 's3BucketBackupType',
  displayName: 'Change detection mode',
  type: PropertyType.SingleValue,
  group: 'backup-details',
  propertyPredicate: (providers) =>
    providers.includes(Provider.Aws) || providers.includes(Provider.Gcp),
  field: {
    Component: ({ entity }) => {
      if (
        entity.resourceType !== ResourceType.AwsS3 &&
        entity.resourceType !== ResourceType.GcpCloudStorageBucket
      ) {
        return null;
      }

      return <S3BucketBackupTypeField item={entity} />;
    },
  },
};

export const s3BucketScanType: FilterProperty<InventoryResource> = {
  name: 's3BucketScanType',
  displayName: 'Scan mode',
  type: PropertyType.SingleValue,
  group: 'backup-details',
  propertyPredicate: (providers) => providers.includes(Provider.Aws),
  field: {
    Component: ({ entity }) => {
      if (entity.resourceType !== ResourceType.AwsS3) {
        return null;
      }

      return <S3BucketScanTypeField item={entity} />;
    },
  },
};
